/**
 * AgentsFlow Constants
 * Central definition of business types, capabilities, and configurations
 */

// ===============================================
// LLM PROVIDERS & MODELS
// ===============================================

export const LLM_PROVIDERS = {
  openai: {
    id: 'openai',
    name: 'OpenAI',
    icon: 'IconBrandOpenai',
    description: 'Modelos GPT da OpenAI',
    models: [
      { id: 'gpt-4o-mini', name: 'GPT-4o Mini', description: 'Mais econômico, ótimo custo-benefício' },
      { id: 'gpt-4.1', name: 'GPT-4.1', description: 'Última geração do GPT-4' },
      { id: 'gpt-4.1-mini', name: 'GPT-4.1 Mini', description: 'Versão compacta do GPT-4.1', recommended: true },
      { id: 'gpt-5', name: 'GPT-5', description: 'Modelo mais avançado da OpenAI' },
      { id: 'gpt-5-mini', name: 'GPT-5 Mini', description: 'Versão compacta do GPT-5' },
      { id: 'gpt-5.2', name: 'GPT-5.2', description: 'Versão mais recente com melhorias incrementais' },
    ],
  },
  anthropic: {
    id: 'anthropic',
    name: 'Anthropic',
    icon: 'IconSparkles',
    description: 'Modelos Claude da Anthropic',
    models: [
      { id: 'claude-haiku-4-5-20251001', name: 'Claude Haiku 4.5', description: 'Rápido e econômico' },
      { id: 'claude-sonnet-4-5-20250929', name: 'Claude Sonnet 4.5', description: 'Inteligente e versátil', recommended: true },
      { id: 'claude-opus-4-20250514', name: 'Claude Opus 4', description: 'Robusto em raciocínio e código' },
    ],
  },
  google: {
    id: 'google',
    name: 'Google AI',
    icon: 'IconBrandGoogle',
    description: 'Modelos Gemini do Google',
    models: [
      { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', description: 'Rápido e eficiente', recommended: true },
      { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', description: 'Mais capaz e versátil' },
      { id: 'gemini-2.5-flash-lite', name: 'Gemini 2.5 Flash Lite', description: 'Mais rápido, otimizado para custo e baixa latência' },
    ],
  },
};

export const LLM_SETTINGS = {
  max_tokens: {
    label: 'Máximo de Tokens',
    description: 'Limite máximo de tokens na resposta',
    type: 'number',
    min: 256,
    max: 8192,
    default: 4096,
  },
  fallback_enabled: {
    label: 'Fallback Automático',
    description: 'Se o provedor principal falhar, usar outro automaticamente',
    type: 'boolean',
    default: true,
  },
};

// ===============================================
// SMART BODY MODELS (for API call body formatter)
// ===============================================

export const SMART_BODY_MODELS = [
  { id: 'gpt-4.1-mini', name: 'GPT-4.1 Mini', provider: 'openai', recommended: true },
  { id: 'gpt-4o-mini', name: 'GPT-4o Mini', provider: 'openai' },
  { id: 'gemini-2.5-flash-lite', name: 'Gemini 2.5 Flash Lite', provider: 'google' },
  { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', provider: 'google' },
  { id: 'claude-haiku-4-5-20251001', name: 'Claude Haiku 4.5', provider: 'anthropic' },
];

// ===============================================
// SCHEDULING FIELD OPTIONS (for react-select)
// ===============================================

export const SCHEDULING_FIELD_OPTIONS = [
  {
    label: '👤 Dados Pessoais',
    options: [
      { value: 'name', label: 'Nome completo' },
      { value: 'first_name', label: 'Primeiro nome' },
      { value: 'birth_date', label: 'Data de nascimento' },
      { value: 'age', label: 'Idade' },
      { value: 'gender', label: 'Gênero' },
      { value: 'marital_status', label: 'Estado civil' },
      { value: 'nationality', label: 'Nacionalidade' },
      { value: 'profession', label: 'Profissão' },
    ],
  },
  {
    label: '📄 Documentos',
    options: [
      { value: 'cpf', label: 'CPF' },
      { value: 'rg', label: 'RG' },
      { value: 'cnh', label: 'CNH' },
      { value: 'passport', label: 'Passaporte' },
      { value: 'voter_id', label: 'Título de eleitor' },
      { value: 'work_card', label: 'Carteira de trabalho' },
    ],
  },
  {
    label: '📞 Contato',
    options: [
      { value: 'phone', label: 'Telefone/Celular' },
      { value: 'phone_secondary', label: 'Telefone secundário' },
      { value: 'whatsapp', label: 'WhatsApp' },
      { value: 'email', label: 'E-mail' },
      { value: 'instagram', label: 'Instagram' },
    ],
  },
  {
    label: '💳 Pagamento (apenas Agendamento)',
    options: [
      { value: 'payment_status', label: 'Sinal de pagamento (status)' },
      { value: 'payment_receipt', label: 'Comprovante de pagamento' },
    ],
  },
  {
    label: '📍 Endereço',
    options: [
      { value: 'zip_code', label: 'CEP' },
      { value: 'street', label: 'Rua/Logradouro' },
      { value: 'street_number', label: 'Número' },
      { value: 'complement', label: 'Complemento' },
      { value: 'neighborhood', label: 'Bairro' },
      { value: 'city', label: 'Cidade' },
      { value: 'state', label: 'Estado' },
      { value: 'full_address', label: 'Endereço completo' },
    ],
  },
  {
    label: '🏥 Saúde / Clínicas',
    options: [
      { value: 'insurance', label: 'Convênio/Plano de saúde' },
      { value: 'insurance_card', label: 'Nº carteirinha do convênio' },
      { value: 'blood_type', label: 'Tipo sanguíneo' },
      { value: 'allergies', label: 'Alergias' },
      { value: 'medications', label: 'Medicamentos em uso' },
      { value: 'medical_conditions', label: 'Condições médicas' },
      { value: 'emergency_contact', label: 'Contato de emergência' },
      { value: 'emergency_phone', label: 'Telefone de emergência' },
      { value: 'weight', label: 'Peso' },
      { value: 'height', label: 'Altura' },
      { value: 'symptoms', label: 'Sintomas/Queixa' },
    ],
  },
  {
    label: '🚗 Veículos / Auto',
    options: [
      { value: 'vehicle_plate', label: 'Placa do veículo' },
      { value: 'vehicle_brand', label: 'Marca do veículo' },
      { value: 'vehicle_model', label: 'Modelo do veículo' },
      { value: 'vehicle_year', label: 'Ano do veículo' },
      { value: 'vehicle_color', label: 'Cor do veículo' },
      { value: 'vehicle_chassis', label: 'Chassi' },
      { value: 'vehicle_renavam', label: 'Renavam' },
      { value: 'vehicle_km', label: 'Quilometragem' },
      { value: 'vehicle_issue', label: 'Problema/Defeito relatado' },
    ],
  },
  {
    label: '🏠 Imobiliário',
    options: [
      { value: 'property_type', label: 'Tipo de imóvel' },
      { value: 'property_purpose', label: 'Finalidade (compra/aluguel)' },
      { value: 'property_rooms', label: 'Quantidade de quartos' },
      { value: 'property_budget', label: 'Orçamento/Faixa de valor' },
      { value: 'property_region', label: 'Região de interesse' },
      { value: 'property_size', label: 'Metragem desejada' },
    ],
  },
  {
    label: '🐾 Pet / Veterinário',
    options: [
      { value: 'pet_name', label: 'Nome do pet' },
      { value: 'pet_species', label: 'Espécie (cão, gato, etc.)' },
      { value: 'pet_breed', label: 'Raça' },
      { value: 'pet_age', label: 'Idade do pet' },
      { value: 'pet_weight', label: 'Peso do pet' },
      { value: 'pet_gender', label: 'Sexo do pet' },
      { value: 'pet_vaccinated', label: 'Vacinação em dia?' },
      { value: 'pet_symptoms', label: 'Sintomas do pet' },
    ],
  },
  {
    label: '🎓 Educação',
    options: [
      { value: 'student_name', label: 'Nome do aluno' },
      { value: 'enrollment_number', label: 'Número de matrícula' },
      { value: 'course', label: 'Curso' },
      { value: 'class_group', label: 'Turma' },
      { value: 'school_year', label: 'Ano escolar' },
      { value: 'guardian_name', label: 'Nome do responsável' },
      { value: 'guardian_cpf', label: 'CPF do responsável' },
      { value: 'guardian_phone', label: 'Telefone do responsável' },
    ],
  },
  {
    label: '💼 Profissional / Empresarial',
    options: [
      { value: 'company_name', label: 'Nome da empresa' },
      { value: 'cnpj', label: 'CNPJ' },
      { value: 'job_title', label: 'Cargo' },
      { value: 'department', label: 'Departamento' },
      { value: 'employee_id', label: 'Matrícula funcional' },
      { value: 'work_phone', label: 'Telefone comercial' },
      { value: 'work_email', label: 'E-mail corporativo' },
    ],
  },
  {
    label: '💇 Beleza / Estética',
    options: [
      { value: 'hair_type', label: 'Tipo de cabelo' },
      { value: 'skin_type', label: 'Tipo de pele' },
      { value: 'service_preference', label: 'Preferência de serviço' },
      { value: 'last_visit', label: 'Data da última visita' },
    ],
  },
  {
    label: '🍽️ Restaurante / Eventos',
    options: [
      { value: 'party_size', label: 'Quantidade de pessoas' },
      { value: 'dietary_restrictions', label: 'Restrições alimentares' },
      { value: 'food_preferences', label: 'Preferências alimentares' },
      { value: 'event_type', label: 'Tipo de evento' },
      { value: 'special_occasion', label: 'Ocasião especial' },
      { value: 'seating_preference', label: 'Preferência de mesa/lugar' },
    ],
  },
  {
    label: '📅 Preferências de Agendamento',
    options: [
      { value: 'professional_preference', label: 'Profissional de preferência' },
      { value: 'preferred_day', label: 'Dia preferido' },
      { value: 'preferred_date', label: 'Data preferida' },
      { value: 'preferred_time_slot', label: 'Horário preferido' },
      { value: 'preferred_period', label: 'Período preferido (manhã/tarde/noite)' },
      { value: 'service_type', label: 'Tipo de atendimento/serviço' },
      { value: 'appointment_reason', label: 'Motivo da consulta/visita' },
      { value: 'urgency_level', label: 'Nível de urgência' },
      { value: 'first_visit', label: 'É primeira vez?' },
      { value: 'returning_customer', label: 'Cliente retornando?' },
    ],
  },
  {
    label: '📝 Outros',
    options: [
      { value: 'referral_source', label: 'Como nos conheceu?' },
      { value: 'notes', label: 'Observações' },
      { value: 'special_needs', label: 'Necessidades especiais' },
      { value: 'preferred_contact', label: 'Forma de contato preferida' },
      { value: 'preferred_time', label: 'Horário de preferência' },
      { value: 'budget', label: 'Orçamento disponível' },
    ],
  },
];

// Helper to get flat options for validation
export const SCHEDULING_FIELD_VALUES = SCHEDULING_FIELD_OPTIONS.flatMap(
  group => group.options.map(opt => opt.value)
);

// Helper to get label by value
export const getSchedulingFieldLabel = (value) => {
  for (const group of SCHEDULING_FIELD_OPTIONS) {
    const option = group.options.find(opt => opt.value === value);
    if (option) return option.label;
  }
  return value;
};

// ===============================================
// AGENT ADVANCED SETTINGS
// ===============================================

export const AGENT_ADVANCED_SETTINGS = {
  // Execution settings
  max_iterations: {
    key: 'max_iterations',
    label: 'Máximo de Iterações',
    description: 'Número máximo de ciclos que o agente pode executar por mensagem',
    type: 'number',
    default: 10,
    min: 1,
    max: 50,
    category: 'execution',
  },
  planning_enabled: {
    key: 'planning_enabled',
    label: 'Planejamento Habilitado',
    description: 'O agente planeja antes de executar (melhora qualidade mas aumenta latência)',
    type: 'boolean',
    default: true,
    category: 'execution',
  },
  subagent_enabled: {
    key: 'subagent_enabled',
    label: 'Sub-agentes Habilitados',
    description: 'Permite que o agente delegue tarefas para sub-agentes especializados',
    type: 'boolean',
    default: true,
    category: 'execution',
  },
  // Human-in-the-loop settings
  hitl_enabled: {
    key: 'hitl_enabled',
    label: 'Aprovação Humana (HITL)',
    description: 'Algumas ações requerem aprovação humana antes de executar',
    type: 'boolean',
    default: true,
    category: 'hitl',
  },
  hitl_timeout: {
    key: 'hitl_timeout',
    label: 'Timeout de Aprovação (segundos)',
    description: 'Tempo máximo para aguardar aprovação humana',
    type: 'number',
    default: 300,
    min: 60,
    max: 3600,
    category: 'hitl',
  },
  hitl_actions: {
    key: 'hitl_actions',
    label: 'Ações que Requerem Aprovação',
    description: 'Selecione quais ações precisam de aprovação humana',
    type: 'multiselect',
    options: ['cancel_appointment', 'reschedule_appointment', 'create_order', 'transfer_to_human', 'apply_discount'],
    optionLabels: {
      cancel_appointment: 'Cancelar agendamento',
      reschedule_appointment: 'Remarcar agendamento',
      create_order: 'Criar pedido',
      transfer_to_human: 'Transferir para humano',
      apply_discount: 'Aplicar desconto',
    },
    default: ['cancel_appointment', 'create_order'],
    category: 'hitl',
  },
  // Memory settings
  memory_ttl_hours: {
    key: 'memory_ttl_hours',
    label: 'Tempo de Memória (horas)',
    description: 'Por quanto tempo o agente lembra das conversas',
    type: 'number',
    default: 168,
    min: 1,
    max: 720,
    category: 'memory',
  },
  short_term_limit: {
    key: 'short_term_limit',
    label: 'Limite de Mensagens Recentes',
    description: 'Quantas mensagens recentes manter no contexto',
    type: 'number',
    default: 30,
    min: 5,
    max: 100,
    category: 'memory',
  },
  compression_threshold: {
    key: 'compression_threshold',
    label: 'Threshold de Compressão',
    description: 'Após quantas mensagens comprimir a memória',
    type: 'number',
    default: 20,
    min: 10,
    max: 50,
    category: 'memory',
  },
};

export const ADVANCED_SETTINGS_CATEGORIES = [
  { id: 'execution', name: 'Execução', icon: 'IconCpu', description: 'Controle de execução do agente' },
  { id: 'hitl', name: 'Aprovação Humana', icon: 'IconUserCheck', description: 'Human-in-the-loop' },
  { id: 'memory', name: 'Memória', icon: 'IconBrain', description: 'Configurações de memória' },
];

// ===============================================
// KNOWLEDGE BASE SETTINGS
// ===============================================

export const KNOWLEDGE_SETTINGS = {
  chunk_size: {
    key: 'chunk_size',
    label: 'Tamanho do Chunk',
    description: 'Tamanho de cada pedaço de texto indexado',
    type: 'number',
    default: 1000,
    min: 200,
    max: 4000,
  },
  chunk_overlap: {
    key: 'chunk_overlap',
    label: 'Sobreposição de Chunks',
    description: 'Quantos caracteres de sobreposição entre chunks',
    type: 'number',
    default: 200,
    min: 0,
    max: 500,
  },
  embedding_model: {
    key: 'embedding_model',
    label: 'Modelo de Embedding',
    description: 'Modelo usado para vetorização',
    type: 'select',
    options: [
      { value: 'text-embedding-3-small', label: 'OpenAI Small (Recomendado)' },
      { value: 'text-embedding-3-large', label: 'OpenAI Large (Maior precisão)' },
      { value: 'text-embedding-ada-002', label: 'OpenAI Ada (Legacy)' },
    ],
    default: 'text-embedding-3-small',
  },
  min_score: {
    key: 'min_score',
    label: 'Score Mínimo de Relevância',
    description: 'Score mínimo para considerar um resultado relevante (0-1)',
    type: 'range',
    min: 0,
    max: 1,
    step: 0.05,
    default: 0.7,
  },
  max_results: {
    key: 'max_results',
    label: 'Máximo de Resultados',
    description: 'Quantidade máxima de chunks retornados',
    type: 'number',
    default: 3,
    min: 1,
    max: 10,
  },
};

export const KNOWLEDGE_SOURCE_TYPES = {
  file: { id: 'file', name: 'Documento', icon: 'IconFile', description: 'PDF, DOCX, XLSX, CSV, TXT' },
  website: { id: 'website', name: 'Website', icon: 'IconWorld', description: 'Extrair conteúdo de páginas web' },
  google_sheets: { id: 'google_sheets', name: 'Google Sheets', icon: 'IconBrandGoogleDrive', description: 'Planilhas do Google' },
  google_drive: { id: 'google_drive', name: 'Google Drive', icon: 'IconBrandGoogleDrive', description: 'Pastas do Google Drive' },
  // notion: { id: 'notion', name: 'Notion', icon: 'IconBrandNotion', description: 'Páginas e databases do Notion' },
};

export const WEBSITE_SCRAPING_OPTIONS = {
  max_depth: {
    key: 'max_depth',
    label: 'Profundidade Máxima',
    description: 'Quantos níveis de links seguir',
    type: 'number',
    default: 1,
    min: 0,
    max: 5,
  },
  max_pages: {
    key: 'max_pages',
    label: 'Máximo de Páginas',
    description: 'Limite de páginas a extrair',
    type: 'number',
    default: 10,
    min: 1,
    max: 100,
  },
  include_patterns: {
    key: 'include_patterns',
    label: 'Padrões de Inclusão (URLs)',
    description: 'Apenas URLs que contenham estes padrões (um por linha)',
    type: 'textarea',
    placeholder: '/blog/\n/docs/',
  },
  exclude_patterns: {
    key: 'exclude_patterns',
    label: 'Padrões de Exclusão (URLs)',
    description: 'Ignorar URLs que contenham estes padrões (um por linha)',
    type: 'textarea',
    placeholder: '/admin/\n/login/',
  },
  selector: {
    key: 'selector',
    label: 'Seletor CSS (opcional)',
    description: 'Extrair apenas conteúdo dentro deste seletor',
    type: 'text',
    placeholder: 'article, main, .content',
  },
};

// ===============================================
// NOTIFICATION TEMPLATES
// ===============================================

export const NOTIFICATION_CHANNELS = [
  { id: 'whatsapp', name: 'WhatsApp', icon: 'IconBrandWhatsapp', description: 'Enviar via WhatsApp' },
  { id: 'email', name: 'Email', icon: 'IconMail', description: 'Enviar via Email' },
];

export const NOTIFICATION_VARIABLES = [
  { id: 'client_name', label: 'Nome do cliente' },
  { id: 'client_phone', label: 'Telefone do cliente' },
  { id: 'client_email', label: 'Email do cliente' },
  { id: 'appointment_date', label: 'Data do agendamento' },
  { id: 'appointment_time', label: 'Horário do agendamento' },
  { id: 'professional_name', label: 'Nome do profissional' },
  { id: 'service_name', label: 'Nome do serviço' },
  { id: 'reason', label: 'Motivo' },
  { id: 'crm_provider', label: 'CRM utilizado' },
];

// ===============================================
// NOTIFICATION EVENTS (Predefined triggers)
// ===============================================

export const NOTIFICATION_EVENTS = [
  {
    id: 'appointment_created',
    label: 'Novo agendamento',
    description: 'Quando um novo agendamento for realizado',
    variables: ['client_name', 'client_phone', 'appointment_date', 'appointment_time', 'professional_name'],
    defaultTemplate: 'Novo agendamento!\n\nCliente: {client_name}\nTelefone: {client_phone}\nData: {appointment_date}\nHorario: {appointment_time}\nProfissional: {professional_name}',
  },
  {
    id: 'appointment_canceled',
    label: 'Agendamento cancelado',
    description: 'Quando um agendamento for cancelado',
    variables: ['client_name', 'client_phone', 'reason'],
    defaultTemplate: 'Agendamento cancelado!\n\nCliente: {client_name}\nTelefone: {client_phone}\nMotivo: {reason}',
  },
  {
    id: 'appointment_rescheduled',
    label: 'Agendamento remarcado',
    description: 'Quando um agendamento for remarcado',
    variables: ['client_name', 'client_phone', 'appointment_date', 'appointment_time', 'professional_name', 'reason'],
    defaultTemplate: 'Agendamento remarcado!\n\nCliente: {client_name}\nTelefone: {client_phone}\nNova data: {appointment_date}\nNovo horario: {appointment_time}\nProfissional: {professional_name}',
  },
  {
    id: 'lead_sent_to_crm',
    label: 'Lead enviado ao CRM',
    description: 'Quando um lead for enviado ao CRM configurado',
    variables: ['client_name', 'client_phone', 'client_email', 'crm_provider'],
    defaultTemplate: 'Novo lead no CRM!\n\nNome: {client_name}\nTelefone: {client_phone}\nEmail: {client_email}\nCRM: {crm_provider}',
  },
];

// ===============================================
// PRODUCT CATALOG SETTINGS
// ===============================================

export const PRODUCT_CATALOG_SOURCES = [
  { id: 'manual', name: 'Manual', description: 'Cadastrar produtos manualmente', icon: 'IconEdit' },
  { id: 'google_sheets', name: 'Google Sheets', description: 'Importar de planilha', icon: 'IconBrandGoogleDrive' },
  { id: 'api', name: 'API Externa', description: 'Integrar com sistema de estoque', icon: 'IconApi' },
];

export const PRODUCT_FIELDS = [
  { id: 'name', name: 'Nome', required: true },
  { id: 'description', name: 'Descrição', required: false },
  { id: 'price', name: 'Preço', required: true, type: 'number' },
  { id: 'sku', name: 'SKU/Código', required: false },
  { id: 'category', name: 'Categoria', required: false },
  { id: 'stock', name: 'Estoque', required: false, type: 'number' },
  { id: 'image_url', name: 'URL da Imagem', required: false },
];

// ===============================================
// SHIPPING PROVIDERS
// ===============================================

export const SHIPPING_PROVIDERS = {
  correios: {
    id: 'correios',
    name: 'Correios',
    icon: 'IconMail',
    description: 'Integração com API dos Correios',
    configFields: [
      { key: 'cep_origem', label: 'CEP de Origem', type: 'text', required: true },
      { key: 'contrato', label: 'Número do Contrato (opcional)', type: 'text' },
      { key: 'servicos', label: 'Serviços', type: 'multiselect', options: ['SEDEX', 'PAC', 'SEDEX 10', 'SEDEX 12'], default: ['SEDEX', 'PAC'] },
    ],
  },
  jadlog: {
    id: 'jadlog',
    name: 'JadLog',
    icon: 'IconTruck',
    description: 'Integração com JadLog',
    configFields: [
      { key: 'cnpj', label: 'CNPJ', type: 'text', required: true },
      { key: 'api_key', label: 'Token de API', type: 'password', required: true },
      { key: 'modalidade', label: 'Modalidade', type: 'select', options: ['EXPRESSO', 'RODOVIÁRIO', 'PACKAGE'], default: 'RODOVIÁRIO' },
    ],
  },
  melhor_envio: {
    id: 'melhor_envio',
    name: 'Melhor Envio',
    icon: 'IconPackage',
    description: 'Agregador de fretes',
    configFields: [
      { key: 'token', label: 'Token de Acesso', type: 'password', required: true },
      { key: 'environment', label: 'Ambiente', type: 'select', options: ['sandbox', 'production'], default: 'sandbox' },
    ],
  },
  loggi: {
    id: 'loggi',
    name: 'Loggi',
    icon: 'IconBike',
    description: 'Entregas rápidas',
    configFields: [
      { key: 'api_key', label: 'API Key', type: 'password', required: true },
      { key: 'company_id', label: 'Company ID', type: 'text', required: true },
    ],
  },
  custom: {
    id: 'custom',
    name: 'Personalizado',
    icon: 'IconSettings',
    description: 'Webhook customizado',
    configFields: [
      { key: 'webhook_url', label: 'URL do Webhook', type: 'text', required: true },
      { key: 'auth_token', label: 'Token de Autenticação', type: 'password' },
    ],
  },
};

// ===============================================
// BUSINESS TYPES / SECTORS
// ===============================================

export const BUSINESS_TYPES = [
  {
    id: 'clinic',
    name: 'Clínica de Saúde',
    icon: 'IconStethoscope',
    emoji: '🏥',
    description: 'Consultórios, clínicas médicas e odontológicas',
    defaultCapabilities: ['scheduling', 'cancellation', 'faq', 'notifications'],
  },
  {
    id: 'restaurant',
    name: 'Restaurante / Delivery',
    icon: 'IconToolsKitchen2',
    emoji: '🍽️',
    description: 'Restaurantes, lanchonetes e delivery de comida',
    defaultCapabilities: ['sales', 'faq'],
  },
  {
    id: 'beauty',
    name: 'Salão de Beleza',
    icon: 'IconScissors',
    emoji: '💇',
    description: 'Salões, barbearias e estúdios de estética',
    defaultCapabilities: ['scheduling', 'cancellation', 'faq', 'notifications'],
  },
  {
    id: 'automotive',
    name: 'Oficina / Auto',
    icon: 'IconCar',
    emoji: '🚗',
    description: 'Oficinas mecânicas e serviços automotivos',
    defaultCapabilities: ['scheduling', 'faq'],
  },
  {
    id: 'realestate',
    name: 'Imobiliária',
    icon: 'IconHome',
    emoji: '🏠',
    description: 'Corretores e imobiliárias',
    defaultCapabilities: ['data_collection', 'faq'],
  },
  {
    id: 'ecommerce',
    name: 'E-commerce / Loja',
    icon: 'IconShoppingCart',
    emoji: '🛒',
    description: 'Lojas virtuais e físicas',
    defaultCapabilities: ['sales', 'faq'],
  },
  {
    id: 'education',
    name: 'Educação',
    icon: 'IconSchool',
    emoji: '📚',
    description: 'Escolas, cursos e instituições de ensino',
    defaultCapabilities: ['scheduling', 'faq', 'data_collection'],
  },
  {
    id: 'other',
    name: 'Outro Negócio',
    icon: 'IconBuildingStore',
    emoji: '📦',
    description: 'Outros tipos de negócio',
    defaultCapabilities: ['faq'],
  },
];

// ===============================================
// CAPABILITIES CATALOG
// ===============================================

export const MEDIA_ANALYSIS_PRESETS = {
  payment_receipt: 'Analise este comprovante de pagamento e extraia as seguintes informações:\n- Valor da transação\n- Data e hora do pagamento\n- Banco/instituição de origem e destino\n- Nome do pagador e do beneficiário\n- Tipo de transação (PIX, TED, boleto, cartão)\n- Código de referência/autenticação\nConfirme se o comprovante parece válido e legível.',
  medical_prescription: 'Analise esta receita médica e extraia as seguintes informações:\n- Nome do paciente\n- Nome e CRM do médico\n- Medicamentos prescritos\n- Dosagem e posologia de cada medicamento\n- Validade da receita\nAlerte se alguma informação estiver ilegível.',
  medical_exam: 'Analise este resultado de exame médico e extraia:\n- Nome do paciente\n- Tipo de exame realizado\n- Resultados principais com valores\n- Valores de referência (quando visíveis)\n- Laboratório e data do exame\nDestaque resultados fora dos valores de referência.',
  invoice: 'Analise esta nota fiscal e extraia:\n- Número da NF\n- CNPJ e nome da empresa emissora\n- Itens/serviços listados com valores\n- Valor total e impostos\n- Data de emissão\nConfirme se as informações estão legíveis.',
  identity_document: 'Analise este documento de identidade e extraia:\n- Nome completo\n- Número do documento (exiba apenas os últimos 4 dígitos por segurança)\n- Data de nascimento\n- Data de emissão\n- Órgão emissor\nIMPORTANTE: Por segurança, NÃO exiba o número completo do documento.',
  vehicle_document: 'Analise este documento de veículo e extraia:\n- Placa do veículo\n- Modelo e marca\n- Ano de fabricação/modelo\n- Nome do proprietário\n- Número do RENAVAM\nInforme se há alguma pendência visível.',
  contract: 'Analise este contrato e extraia:\n- Partes envolvidas (contratante e contratado)\n- Objeto do contrato\n- Valor e condições de pagamento\n- Período de vigência\n- Cláusulas principais e penalidades\nResuma os pontos mais importantes.',
  property_document: 'Analise este documento de imóvel e extraia:\n- Endereço completo do imóvel\n- Número de matrícula\n- Área total (m²)\n- Nome do proprietário\n- Situação (regular, pendências)\nInforme se há alguma observação relevante.',
  product_photo: 'Analise esta foto de produto e descreva:\n- Tipo de produto\n- Marca (se visível)\n- Estado de conservação (novo, usado, danificado)\n- Características visíveis (cor, tamanho, material)\n- Qualquer defeito ou detalhe relevante\nSeja objetivo e detalhado na descrição.',
  general: 'Analise o conteúdo enviado e:\n- Descreva todo o conteúdo visível\n- Extraia todos os textos legíveis\n- Identifique o tipo de documento ou imagem\n- Destaque informações importantes\nSeja completo e organizado na resposta.',
};

export const CAPABILITIES = {
  // ==========================================
  // CORE CAPABILITIES
  // ==========================================
  scheduling: {
    code: 'scheduling',
    name: 'Agendamento Completo',
    shortName: 'Agendamento',
    icon: 'IconCalendarEvent',
    emoji: '📅',
    category: 'core',
    description: 'Agendar, verificar disponibilidade, cancelar e remarcar compromissos',
    longDescription: 'O agente pode agendar novos horários, verificar disponibilidade, e também processar cancelamentos e remarcações automaticamente quando o cliente solicitar durante a conversa.',
    configFields: [
      // === SEÇÃO: AGENDAMENTO ===
      { key: 'calendar_provider', label: 'Onde salvar os agendamentos?', type: 'select', options: ['internal', 'google_calendar'], optionLabels: { internal: 'Agenda Interna', google_calendar: 'Google Agenda' }, default: 'internal', tooltip: 'Escolha onde os agendamentos serão salvos. Para usar o Google Agenda, é necessário configurar a integração primeiro na aba "Integrações".', infoAlert: { condition: 'google_calendar', message: 'Para usar o Google Agenda, configure primeiro a integração na aba "Integrações".' } },
      { key: 'max_slots_to_show', label: 'Quantidade de horários a exibir', type: 'number', default: 5, min: 1, max: 8, tooltip: 'Quantidade de horários exibidos ao cliente quando não houver preferência temporal. Se o cliente mencionar um horário específico (ex: "às 14h") ou um período (ex: "de manhã"), o agente prioriza automaticamente os slots próximos à preferência.' },
      { key: 'max_advance_days', label: 'Dias de antecedência máxima', type: 'number', default: 30, tooltip: 'Limite máximo de dias no futuro que o cliente pode agendar.' },
      { key: 'allow_same_day', label: 'Permitir agendamento no mesmo dia?', type: 'boolean', default: true, tooltip: 'Se ativo, o cliente pode agendar para o mesmo dia da conversa. Se desativado, só permite agendamentos a partir do próximo dia.' },
      { key: 'check_availability_enabled', label: 'Verificar disponibilidade antes de agendar?', type: 'boolean', default: true, tooltip: 'Se ativo, o agente verifica conflitos de horário e mostra apenas horários livres. Se desativado, permite múltiplos agendamentos no mesmo horário (ideal para eventos, cursos e palestras).' },
      { key: 'required_fields', label: 'Dados obrigatórios', type: 'grouped_multiselect', optionsRef: 'SCHEDULING_FIELD_OPTIONS', default: ['name', 'phone'], tooltip: 'Dados que o agente deve coletar do cliente antes de finalizar o agendamento.' },
      { key: 'sync_to_crm', label: 'Onde salvar os dados do agendamento?', type: 'select', options: ['disabled', 'internal', 'rdstation', 'google_sheets'], optionLabels: { disabled: 'Não salvar', internal: 'CRM Interno', rdstation: 'RD Station CRM', google_sheets: 'Google Sheets' }, default: 'disabled', tooltip: 'Escolha onde os dados coletados (nome, telefone, CPF, email, etc.) serão salvos automaticamente após o agendamento ser confirmado.', infoAlert: { condition: ['rdstation', 'google_sheets'], message: 'Para usar esta opção, configure primeiro a integração correspondente na aba "Integrações".' } },
      { key: 'generate_conversation_summary', label: 'Gerar resumo da conversa com IA?', type: 'boolean', default: true, tooltip: 'A IA gera um resumo inteligente da conversa e salva no histórico do contato. Útil para o atendente ter contexto do que foi conversado.' },
      { key: 'post_scheduling_response', label: 'Resposta após agendamento', type: 'select', options: ['ai_response', 'custom_message'], optionLabels: { ai_response: 'IA gera resposta', custom_message: 'Mensagem personalizada' }, default: 'ai_response', tooltip: 'Escolha se a IA deve gerar a resposta de confirmação ou se deseja usar uma mensagem fixa personalizada.' },
      { key: 'custom_confirmation_message', label: 'Mensagem de confirmação personalizada', type: 'textarea', default: 'Seu agendamento foi confirmado!\n\n📅 Data: {{date}}\n🕐 Horário: {{time}}\n👤 Profissional: {{professional}}\n\nAté logo!', placeholder: 'Use {{date}}, {{time}}, {{professional}}, {{client_name}} para dados dinâmicos', tooltip: 'Mensagem fixa enviada após o agendamento ser confirmado. Use variáveis entre {{}} para dados dinâmicos.', showIf: { post_scheduling_response: 'custom_message' } },
      { key: 'description_instructions', label: 'Instruções para a IA gerar a descrição do agendamento', type: 'textarea', default: 'Gere uma descrição completa e profissional do agendamento com os seguintes elementos, quando houver dados correspondentes na conversa:\n\n- Resumo inicial com nome do cliente, data, horário, profissional e serviço.\n- Informações de pagamento do sinal (valor e método), caso o cliente tenha enviado comprovante.\n- Motivo da consulta ou procedimento solicitado, se mencionado.\n- Observações ou preferências relevantes informadas pelo cliente.\n- Dados de contato alternativos (e-mail, documento), se tiverem sido coletados.\n\nUse linguagem formal e direta. Se alguma informação não tiver sido coletada, NÃO invente nem use placeholders — simplesmente omita.', placeholder: 'Descreva em linguagem natural o que você quer ver na descrição do agendamento. Ex: "Inclua o motivo da consulta, plano de saúde e valor do sinal pago."', tooltip: 'Instruções em linguagem natural para a IA montar a descrição salva no calendário. A IA usa os dados coletados na conversa automaticamente — você NÃO precisa saber o nome exato de nenhuma variável. Clique no ícone (i) para ver o guia completo.', helpModal: 'description_instructions' },

      // === SEÇÃO: CANCELAMENTO E REMARCAÇÃO ===
      { key: '_section_cancellation', type: 'section', label: 'Cancelamento e Remarcação', description: 'Configure como o agente deve lidar com solicitações de cancelamento ou remarcação durante o atendimento.' },
      { key: 'cancellation_enabled', label: 'Permitir cancelamento durante o atendimento?', type: 'boolean', default: true, tooltip: 'Se ativado, o agente poderá cancelar agendamentos quando o cliente solicitar, em qualquer momento da conversa.' },
      { key: 'reschedule_enabled', label: 'Permitir remarcação durante o atendimento?', type: 'boolean', default: true, tooltip: 'Se ativado, o agente poderá remarcar agendamentos para nova data/horário.', showIf: { cancellation_enabled: true } },
      { key: 'lookup_field', label: 'Campo para identificar agendamentos', type: 'select', options: ['phone', 'cpf', 'email', 'name'], optionLabels: { phone: 'Telefone (automático do WhatsApp)', cpf: 'CPF', email: 'E-mail', name: 'Nome completo' }, default: 'phone', tooltip: 'Campo que o agente usará para localizar agendamentos existentes quando o cliente quiser cancelar ou remarcar.', showIf: { cancellation_enabled: true } },
      { key: 'ask_cancellation_reason', label: 'Pedir motivo do cancelamento?', type: 'boolean', default: false, tooltip: 'Se ativado, o agente perguntará o motivo antes de cancelar.', showIf: { cancellation_enabled: true } },
      // { key: 'min_advance_hours', label: 'Antecedência mínima para cancelar (horas)', type: 'number', default: 24, min: 0, tooltip: 'Quantas horas antes do agendamento o cliente pode cancelar. Se 0, pode cancelar a qualquer momento.', showIf: { cancellation_enabled: true } }, DESATIVADO TEMPORARIAMENTE.
    ],
  },
  check_availability: {
    code: 'check_availability',
    name: 'Verificar Disponibilidade',
    shortName: 'Disponibilidade',
    icon: 'IconCalendarSearch',
    emoji: '🔍',
    category: 'core',
    description: 'Consultar horários disponíveis sem agendar',
    longDescription: 'O agente consulta e informa os horários disponíveis sem realizar o agendamento. Ideal para fluxos onde o cliente precisa verificar disponibilidade antes de outras etapas (ex: pagamento de sinal). O agente detecta automaticamente quando o cliente pede um horário específico (ex: "tem às 14h?") ou um período (ex: "de manhã"), priorizando os slots mais relevantes. O horário escolhido é salvo automaticamente e reutilizado pelo nó de Agendamento.',
    configFields: [
      { key: 'calendar_provider', label: 'Onde consultar disponibilidade?', type: 'select', options: ['internal', 'google_calendar'], optionLabels: { internal: 'Agenda Interna', google_calendar: 'Google Agenda' }, default: 'internal', tooltip: 'Escolha de onde consultar os horários disponíveis. Para usar o Google Agenda, é necessário configurar a integração primeiro na aba "Integrações".', infoAlert: { condition: 'google_calendar', message: 'Para usar o Google Agenda, configure primeiro a integração na aba "Integrações".' } },
      { key: 'ask_professional', label: 'Perguntar com qual profissional?', type: 'select', options: ['auto', 'always', 'never'], optionLabels: { auto: 'Automático (pergunta se houver mais de 1)', always: 'Sempre perguntar', never: 'Nunca (busca em todas as agendas)' }, default: 'auto', tooltip: 'Define se o agente deve perguntar ao cliente com qual profissional deseja verificar disponibilidade. Se "Nunca", mostra horários de todos os profissionais disponíveis.' },
      { key: 'max_slots_to_show', label: 'Quantidade de horários a exibir', type: 'number', default: 5, min: 1, max: 8, tooltip: 'Quantidade de horários exibidos ao cliente quando não houver preferência temporal. Se o cliente mencionar um horário específico (ex: "às 14h") ou um período (ex: "de manhã"), o agente prioriza automaticamente os slots próximos à preferência.' },
      { key: 'max_advance_days', label: 'Dias de antecedência máxima', type: 'number', default: 30, tooltip: 'Limite máximo de dias no futuro que o cliente pode consultar.' },
      { key: 'allow_same_day', label: 'Permitir consulta no mesmo dia?', type: 'boolean', default: true, tooltip: 'Se ativo, mostra horários disponíveis para hoje. Se desativado, só mostra horários a partir de amanhã.' },
      // { key: 'group_by_day', label: 'Agrupar por dia?', type: 'boolean', default: false }, DESATIVADO TEMPORARIAMENTE
    ],
  },
  cancellation: {
    code: 'cancellation',
    name: 'Cancelamento',
    shortName: 'Cancelamento',
    icon: 'IconCalendarOff',
    emoji: '❌',
    category: 'core',
    description: 'Cancelar agendamentos existentes',
    longDescription: 'Nó dedicado EXCLUSIVAMENTE para cancelamento. A IA só tem acesso às tools de busca e cancelamento — não consegue agendar novo, remarcar, ou pedir sinal por engano. Use em fluxos onde o cliente deseja cancelar um agendamento existente.',
    configFields: [
      { key: 'lookup_field', label: 'Campo para identificar o agendamento', type: 'select', options: ['phone', 'cpf', 'email', 'name'], optionLabels: { phone: 'Telefone (automático do WhatsApp)', cpf: 'CPF', email: 'E-mail', name: 'Nome completo' }, default: 'phone', tooltip: 'Qual campo o agente usa para localizar o agendamento do cliente.' },
      { key: 'ask_cancellation_reason', label: 'Pedir motivo do cancelamento?', type: 'boolean', default: false, tooltip: 'Se ativado, o agente pergunta o motivo antes de cancelar.' },
      { key: 'min_advance_hours', label: 'Antecedência mínima para cancelar (horas)', type: 'number', default: 0, min: 0, tooltip: 'Quantas horas antes do agendamento o cliente pode cancelar. 0 = sem restrição.' },
      { key: 'post_cancellation_response', label: 'Resposta após cancelamento', type: 'select', options: ['ai_response', 'custom_message'], optionLabels: { ai_response: 'IA gera resposta', custom_message: 'Mensagem personalizada' }, default: 'ai_response', tooltip: 'Escolha se a IA deve gerar a mensagem de confirmação ou usar uma mensagem fixa.' },
      { key: 'custom_cancellation_message', label: 'Mensagem personalizada', type: 'textarea', default: 'Seu agendamento foi cancelado.\n\nSe precisar marcar outro horário, é só me chamar! 😊', tooltip: 'Mensagem fixa enviada após cancelamento. Use {{date}}, {{time}}, {{professional}} para dados dinâmicos.', showIf: { post_cancellation_response: 'custom_message' } },
    ],
  },
  appointment_confirmation: {
    code: 'appointment_confirmation',
    name: 'Confirmação de Agendamento',
    shortName: 'Confirmação',
    icon: 'IconCalendarCheck',
    emoji: '✅',
    category: 'core',
    description: 'Confirmar presença ou registrar cancelamento de um agendamento',
    longDescription: 'Nó dedicado a registrar a CONFIRMAÇÃO de um agendamento existente. A IA localiza o agendamento do cliente e atualiza o status de confirmação (confirmado / cancelado / não confirmou) — sem criar, remover ou remarcar. Quando a integração com o Google Agenda está ativa, o status também é refletido no evento do Google.',
    configFields: [
      { key: 'lookup_field', label: 'Campo para identificar o agendamento', type: 'select', options: ['phone', 'cpf', 'email', 'name'], optionLabels: { phone: 'Telefone (automático do WhatsApp)', cpf: 'CPF', email: 'E-mail', name: 'Nome completo' }, default: 'phone', tooltip: 'Qual campo o agente usa para localizar o agendamento do cliente.' },
      { key: 'ask_confirmation_reason', label: 'Pedir motivo ao cancelar?', type: 'boolean', default: false, tooltip: 'Se ativado, o agente pergunta o motivo quando o cliente cancela a confirmação.' },
    ],
  },
  reschedule: {
    code: 'reschedule',
    name: 'Reagendamento',
    shortName: 'Reagendamento',
    icon: 'IconCalendarTime',
    emoji: '🔄',
    category: 'core',
    description: 'Remarcar agendamento existente (sem pedir sinal novamente)',
    longDescription: 'Nó dedicado EXCLUSIVAMENTE para remarcação. A IA só tem acesso às tools de busca, verificação de disponibilidade e remarcação — não consegue criar agendamento novo, cancelar, ou pedir sinal novamente. Ideal para rotear via Roteador Inteligente quando o cliente já tem um agendamento e quer só mudar a data/horário.',
    configFields: [
      { key: 'lookup_field', label: 'Campo para identificar o agendamento', type: 'select', options: ['phone', 'cpf', 'email', 'name'], optionLabels: { phone: 'Telefone (automático do WhatsApp)', cpf: 'CPF', email: 'E-mail', name: 'Nome completo' }, default: 'phone', tooltip: 'Qual campo o agente usa para localizar o agendamento do cliente.' },
      { key: 'calendar_provider', label: 'Onde buscar disponibilidade?', type: 'select', options: ['internal', 'google_calendar'], optionLabels: { internal: 'Agenda Interna', google_calendar: 'Google Agenda' }, default: 'internal', tooltip: 'Escolha de onde consultar os novos horários disponíveis. Deve ser o mesmo local onde os agendamentos foram criados.', infoAlert: { condition: 'google_calendar', message: 'Para usar o Google Agenda, configure primeiro a integração na aba "Integrações".' } },
      { key: 'allow_same_day', label: 'Permitir remarcar para o mesmo dia?', type: 'boolean', default: true, tooltip: 'Se desativado, o cliente só pode remarcar a partir de amanhã.' },
      { key: 'max_advance_days', label: 'Dias de antecedência máxima', type: 'number', default: 30, min: 1, tooltip: 'Limite máximo de dias no futuro para nova data.' },
      { key: 'max_slots_to_show', label: 'Quantidade de horários a exibir', type: 'number', default: 5, min: 1, max: 8, tooltip: 'Quantos horários disponíveis mostrar ao cliente ao remarcar.' },
      { key: 'post_reschedule_response', label: 'Resposta após remarcação', type: 'select', options: ['ai_response', 'custom_message'], optionLabels: { ai_response: 'IA gera resposta', custom_message: 'Mensagem personalizada' }, default: 'ai_response', tooltip: 'Escolha se a IA deve gerar a mensagem de confirmação ou usar uma mensagem fixa.' },
      { key: 'custom_reschedule_message', label: 'Mensagem personalizada', type: 'textarea', default: 'Seu agendamento foi remarcado!\n\n📅 Nova data: {{date}}\n🕐 Novo horário: {{time}}\n👤 Profissional: {{professional}}', tooltip: 'Mensagem fixa enviada após remarcação. Use {{date}}, {{time}}, {{professional}} para dados dinâmicos.', showIf: { post_reschedule_response: 'custom_message' } },
    ],
  },
  faq: {
    code: 'faq',
    name: 'Base de Conhecimento',
    shortName: 'Base de Conhecimento',
    icon: 'IconBook',
    emoji: '📚',
    category: 'core',
    description: 'Informar sobre serviços, preços e localização',
    longDescription: 'O agente usa a base de conhecimento para responder perguntas frequentes dos clientes.',
    configFields: [
      { key: 'behavior_prompt_template', label: 'Template de comportamento', type: 'select', default: '',
        options: ['', 'general', 'clinic', 'beauty', 'restaurant', 'ecommerce', 'realestate', 'education', 'legal', 'support', 'sales'],
        optionLabels: {
          '': 'Selecione um template...',
          general: 'Atendimento Geral',
          clinic: 'Clínica / Saúde',
          beauty: 'Beleza / Estética',
          restaurant: 'Restaurante / Alimentação',
          ecommerce: 'E-commerce / Loja',
          realestate: 'Imobiliária',
          education: 'Educação / Cursos',
          legal: 'Jurídico / Advocacia',
          support: 'Suporte Técnico',
          sales: 'Vendas / Comercial',
        },
        tooltip: 'Selecione um template para preencher automaticamente as instruções de comportamento. Você pode editar o texto após selecionar.',
      },
      { key: 'behavior_prompt', label: 'Instruções de comportamento', type: 'textarea', default: '',
        placeholder: 'Descreva como o agente deve se comportar ao responder dúvidas. Ex: tom de voz, nível de detalhe, como lidar com perguntas fora do escopo...',
        tooltip: 'Define como o agente responde usando a base de conhecimento. Se vazio, usa o comportamento padrão.',
      },
      // { key: 'confidence_threshold', label: 'Confiança mínima (%)', type: 'number', default: 90, min: 0, max: 100 }, // DESATIVADO — valor 0.3 hardcoded no backend é suficiente para a maioria dos casos
      { key: 'max_results', label: 'Máximo de resultados', type: 'number', default: 3 },
      // NOTA: o limite de imagens da base de conhecimento foi movido para
      // a aba "Comportamento" do agente (behavior_config.max_images_per_response).
      // Lá ele se aplica a TODAS as capacidades que usam search_knowledge_base
      // (FAQ, captura de lead com base, etc.), não apenas a esta capacidade.
      { key: 'fallback_to_human', label: 'Transferir se não souber?', type: 'boolean', default: true },
      // { key: 'show_sources', label: 'Mostrar fontes da resposta?', type: 'boolean', default: false }, DESATIVADO TEMPORARIAMENTE
    ],
  },
  data_collection: {
    code: 'data_collection',
    name: 'Coletar Dados',
    shortName: 'Coleta',
    icon: 'IconForms',
    emoji: '📝',
    category: 'core',
    description: 'Coletar informações estruturadas do cliente',
    longDescription: 'O agente coleta dados do cliente de forma conversacional e envia automaticamente para o destino configurado (CRM interno, Google Sheets ou RD Station).',
    configFields: [
      // === SEÇÃO: DESTINO ===
      { key: '_section_destination', type: 'section', label: 'Destino dos Dados' },
      {
        key: 'destination',
        label: 'Para onde enviar os dados coletados?',
        type: 'select',
        options: ['internal_crm', 'google_sheets', 'rdstation'],
        optionLabels: {
          internal_crm: 'CRM Interno (Contatos)',
          google_sheets: 'Google Sheets',
          rdstation: 'RD Station CRM',
        },
        default: 'internal_crm',
        tooltip: 'Selecione o destino para onde os dados coletados serão enviados automaticamente.',
      },
      // === SEÇÃO: CAMPOS A COLETAR ===
      { key: '_section_fields', type: 'section', label: 'Campos a Coletar' },
      {
        key: 'required_fields',
        label: 'Campos obrigatórios',
        type: 'grouped_multiselect',
        optionsRef: 'SCHEDULING_FIELD_OPTIONS',
        default: ['name', 'phone'],
        tooltip: 'O agente DEVE coletar estes campos antes de salvar. Usa a mesma lista de campos do agendamento.',
      },
      {
        key: 'optional_fields',
        label: 'Campos opcionais',
        type: 'grouped_multiselect',
        optionsRef: 'SCHEDULING_FIELD_OPTIONS',
        default: [],
        tooltip: 'O agente tentará coletar se possível, mas não são bloqueantes.',
      },
      // === SEÇÃO: COMPORTAMENTO ===
      { key: '_section_behavior', type: 'section', label: 'Comportamento' },
      {
        key: 'validate_fields',
        label: 'Validar campos automaticamente?',
        type: 'boolean',
        default: false,
        tooltip: 'Valida CPF, telefone e email automaticamente e solicita correção se inválido.',
      },
      {
        key: 'confirm_before_save',
        label: 'Confirmar dados antes de salvar?',
        type: 'boolean',
        default: false,
        tooltip: 'Se ativo, o agente confirmará os dados com o cliente antes de enviá-los ao destino.',
      },
      // === SEÇÃO: GESTÃO DE CRM ===
      { key: '_section_crm', type: 'section', label: 'Gestão de CRM (IA)', showIf: { destination: 'internal_crm' } },
      {
        key: 'crm_management_enabled',
        label: 'Habilitar gestão de CRM pela IA',
        type: 'boolean',
        default: false,
        tooltip: 'A IA poderá classificar temperatura, mover estágios, adicionar tags e gerenciar o contato no CRM automaticamente.',
        showIf: { destination: 'internal_crm' },
      },
      {
        key: 'crm_temperature_rules',
        label: 'Regras de temperatura',
        type: 'textarea',
        default: '- Se apenas pediu informações gerais, classificar como "cold" (frio)\n- Se demonstrou interesse real, fez perguntas sobre preço ou prazo, classificar como "warm" (morno)\n- Se disse que quer comprar, fechar ou contratar, classificar como "hot" (quente)',
        helpModal: 'temperature',
        tooltip: 'Instruções para a IA classificar leads como frio, morno ou quente.',
        showIf: { destination: 'internal_crm', crm_management_enabled: true },
      },
      {
        key: 'crm_stage_rules',
        label: 'Regras de movimentação de estágio',
        type: 'textarea',
        default: '- Quando o contato for criado, manter no estágio inicial\n- Se demonstrou interesse e pediu proposta, mover para "Negociação"\n- Se confirmou a compra ou fechou o negócio, mover para "Ganho"\n- Se disse que não quer ou desistiu, mover para "Perdido"',
        helpModal: 'stage',
        tooltip: 'Instruções para a IA mover o contato entre estágios do pipeline.',
        showIf: { destination: 'internal_crm', crm_management_enabled: true },
      },
      {
        key: 'crm_generate_summary',
        label: 'Gerar resumo da conversa?',
        type: 'boolean',
        default: false,
        tooltip: 'A IA gera um resumo da conversa e salva como nota interna no contato.',
        showIf: { destination: 'internal_crm', crm_management_enabled: true },
      },
      {
        key: 'crm_auto_tags',
        label: 'Tags automáticas?',
        type: 'boolean',
        default: false,
        tooltip: 'A IA cria tags automaticamente baseadas no contexto da conversa (interesse, produto, origem).',
        showIf: { destination: 'internal_crm', crm_management_enabled: true },
      },
    ],
  },

  // ==========================================
  // QUALIFICATION CAPABILITIES
  // ==========================================
  qualify_lead: {
    code: 'qualify_lead',
    name: 'Qualificação de Leads',
    shortName: 'Qualificação',
    icon: 'IconTarget',
    emoji: '🎯',
    category: 'qualification',
    description: 'Qualifica leads automaticamente com scoring',
    longDescription: 'O agente qualifica leads usando frameworks como BANT ou SIMPLE, calcula pontuação e sincroniza com CRM.',
    isAdvanced: true,
    configFields: [
      { key: 'framework', label: 'Framework de qualificação', type: 'select', options: ['simple', 'bant', 'meddic', 'champ'], default: 'simple' },
      { key: 'required_fields', label: 'Dados obrigatórios', type: 'multiselect', options: ['name', 'email', 'phone', 'company', 'role', 'budget'], default: ['name', 'email', 'phone'] },
      { key: 'conversational_collection', label: 'Coleta conversacional?', type: 'boolean', default: true },
      { key: 'max_questions_per_message', label: 'Perguntas por mensagem', type: 'number', default: 2, min: 1, max: 5 },
      { key: 'hot_threshold', label: 'Pontuação para Lead Quente', type: 'number', default: 80, min: 0, max: 100 },
      { key: 'warm_threshold', label: 'Pontuação para Lead Morno', type: 'number', default: 50, min: 0, max: 100 },
      { key: 'cold_threshold', label: 'Pontuação para Lead Frio', type: 'number', default: 20, min: 0, max: 100 },
      { key: 'crm_enabled', label: 'Sincronizar com CRM?', type: 'boolean', default: true },
      { key: 'crm_provider', label: 'Provedor CRM', type: 'select', options: ['rdstation', 'hubspot', 'pipedrive', 'salesforce'], default: 'rdstation' },
      { key: 'auto_sync', label: 'Sincronização automática?', type: 'boolean', default: true },
      { key: 'create_deal_on_hot', label: 'Criar negócio em lead quente?', type: 'boolean', default: true },
      { key: 'notify_sales_on_hot', label: 'Notificar vendas em lead quente?', type: 'boolean', default: true },
      { key: 'notification_channels', label: 'Canais de notificação', type: 'multiselect', options: ['whatsapp', 'email'], default: ['whatsapp'] },
      { key: 'tag_by_score', label: 'Adicionar tags por pontuação?', type: 'boolean', default: true },
    ],
  },

  // ==========================================
  // AUTOMATION CAPABILITIES
  // ==========================================
  notifications: {
    code: 'notifications',
    name: 'Notificações ao Proprietário',
    shortName: 'Notificações',
    icon: 'IconBell',
    emoji: '🔔',
    category: 'automation',
    description: 'Notificar o proprietário sobre eventos configuráveis',
    longDescription: 'O agente notifica o proprietário do negócio via WhatsApp ou email conforme regras configuradas na aba Notificações.',
    configFields: [
      { key: 'owner_notifications', label: 'Notificações habilitadas?', type: 'boolean', default: true },
      { key: 'channels', label: 'Canais de envio', type: 'multiselect', options: ['whatsapp', 'email'], default: ['whatsapp'] },
    ],
  },
  presence_confirmation: {
    code: 'presence_confirmation',
    name: 'Confirmação de Presença',
    shortName: 'Confirmação',
    icon: 'IconUserCheck',
    emoji: '✅',
    category: 'automation',
    description: 'Confirma presença automaticamente antes do agendamento',
    longDescription: 'O agente envia solicitação de confirmação de presença e processa respostas automaticamente, podendo cancelar ou reagendar.',
    isAdvanced: true,
    configFields: [
      { key: 'confirmation_hours_before', label: 'Horas antes para confirmar', type: 'number', default: 24 },
      { key: 'reminder_hours_before', label: 'Horas antes para lembrete', type: 'number', default: 4 },
      { key: 'channels', label: 'Canais de envio', type: 'multiselect', options: ['whatsapp', 'email'], default: ['whatsapp'] },
      { key: 'auto_cancel_if_no_response', label: 'Cancelar se não responder?', type: 'boolean', default: false },
      { key: 'hours_to_wait_response', label: 'Horas para aguardar resposta', type: 'number', default: 12 },
      { key: 'notify_owner_on_confirmation', label: 'Notificar ao confirmar?', type: 'boolean', default: true },
      { key: 'notify_owner_on_cancellation', label: 'Notificar ao cancelar?', type: 'boolean', default: true },
      { key: 'notify_owner_on_no_response', label: 'Notificar se não responder?', type: 'boolean', default: true },
      { key: 'allow_reschedule_on_cancel', label: 'Permitir remarcar ao cancelar?', type: 'boolean', default: true },
      { key: 'custom_confirmation_message', label: 'Mensagem personalizada', type: 'textarea', default: '' },
    ],
  },
  followup_auto: {
    code: 'followup_auto',
    name: 'Follow-up Automático',
    shortName: 'Follow-up',
    icon: 'IconRepeat',
    emoji: '🔄',
    category: 'automation',
    description: 'Acompanhamento automático de leads e clientes',
    longDescription: 'O agente realiza follow-up automático para nurturing de leads, carrinho abandonado, pós-venda e clientes inativos.',
    isAdvanced: true,
    configFields: [
      { key: 'followup_types', label: 'Tipos de follow-up', type: 'multiselect',
        options: ['lead_nurturing', 'abandoned_cart', 'post_sale', 'inactive_customer', 'no_response'],
        optionLabels: {
          lead_nurturing: 'Nutrição de Leads',
          abandoned_cart: 'Carrinho Abandonado',
          post_sale: 'Pós-Venda',
          inactive_customer: 'Cliente Inativo',
          no_response: 'Sem Resposta'
        },
        default: ['lead_nurturing', 'no_response'] },
      { key: 'first_followup_hours', label: 'Primeiro follow-up (horas)', type: 'number', default: 24 },
      { key: 'second_followup_hours', label: 'Segundo follow-up (horas)', type: 'number', default: 72 },
      { key: 'third_followup_hours', label: 'Terceiro follow-up (horas)', type: 'number', default: 168 },
      { key: 'max_followups', label: 'Máximo de tentativas', type: 'number', default: 3, min: 1, max: 10 },
      { key: 'cancel_on_response', label: 'Cancelar se responder?', type: 'boolean', default: true },
      { key: 'channels', label: 'Canais de envio', type: 'multiselect', options: ['whatsapp', 'email'], default: ['whatsapp'] },
      { key: 'allowed_hours_start', label: 'Horário início permitido', type: 'time', default: '09:00' },
      { key: 'allowed_hours_end', label: 'Horário fim permitido', type: 'time', default: '18:00' },
      { key: 'allowed_days', label: 'Dias permitidos', type: 'multiselect', options: ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'], default: ['seg', 'ter', 'qua', 'qui', 'sex'] },
      { key: 'sync_with_crm', label: 'Sincronizar com CRM?', type: 'boolean', default: true },
      { key: 'update_lead_stage', label: 'Atualizar estágio do lead?', type: 'boolean', default: true },
    ],
  },

  // ==========================================
  // COMMERCE CAPABILITIES
  // ==========================================
  // sales: {
  //   code: 'sales',
  //   name: 'Vendas',
  //   shortName: 'Vendas',
  //   icon: 'IconShoppingCart',
  //   emoji: '🛒',
  //   category: 'commerce',
  //   description: 'Apresentar produtos, coletar pedidos e processar vendas',
  //   longDescription: 'O agente apresenta catálogo de produtos, responde dúvidas, gerencia carrinho e processa pedidos.',
  //   configFields: [
  //     { key: 'show_prices', label: 'Mostrar preços?', type: 'boolean', default: true },
  //     { key: 'catalog_source', label: 'Fonte do catálogo', type: 'select', options: ['upload', 'integration', 'google_sheets'], default: 'upload' },
  //     { key: 'enable_cart', label: 'Gerenciar carrinho?', type: 'boolean', default: true },
  //     { key: 'enable_upsell', label: 'Sugestões de upsell?', type: 'boolean', default: true },
  //     { key: 'enable_cross_sell', label: 'Sugestões de cross-sell?', type: 'boolean', default: true },
  //     { key: 'require_confirmation', label: 'Confirmar antes de finalizar?', type: 'boolean', default: true },
  //     { key: 'payment_methods', label: 'Formas de pagamento', type: 'multiselect', options: ['pix', 'cartao_credito', 'cartao_debito', 'boleto', 'dinheiro'], default: ['pix', 'cartao_credito'] },
  //     { key: 'payment_info', label: 'Informar formas de pagamento?', type: 'boolean', default: true },
  //     { key: 'min_order_value', label: 'Valor mínimo do pedido (R$)', type: 'number', default: 0 },
  //     { key: 'allow_discounts', label: 'Permitir descontos?', type: 'boolean', default: false },
  //     { key: 'max_discount_percent', label: 'Desconto máximo (%)', type: 'number', default: 10, showIf: { allow_discounts: true } },
  //     { key: 'shipping_enabled', label: 'Calcular frete?', type: 'boolean', default: true },
  //     { key: 'shipping_provider', label: 'Provedor de frete', type: 'select', options: ['correios', 'jadlog', 'custom', 'none'], default: 'correios' },
  //     { key: 'notify_on_order', label: 'Notificar novo pedido?', type: 'boolean', default: true },
  //   ],
  // },
  delivery_tracking: {
    code: 'delivery_tracking',
    name: 'Rastreamento de Entregas',
    shortName: 'Rastreamento',
    icon: 'IconTruck',
    emoji: '🚚',
    category: 'commerce',
    description: 'Informar status de entregas e rastreamento',
    longDescription: 'O agente consulta e informa o status de entregas, rastreamento e previsão de entrega.',
    configFields: [
      { key: 'tracking_providers', label: 'Provedores de rastreamento', type: 'multiselect', options: ['correios', 'jadlog', 'fedex', 'custom'], default: ['correios'] },
      { key: 'allow_by_order_id', label: 'Consultar por número do pedido?', type: 'boolean', default: true },
      { key: 'allow_by_tracking_code', label: 'Consultar por código de rastreio?', type: 'boolean', default: true },
      { key: 'show_delivery_estimate', label: 'Mostrar previsão de entrega?', type: 'boolean', default: true },
      { key: 'notify_on_status_change', label: 'Notificar mudança de status?', type: 'boolean', default: true },
    ],
  },

  // ==========================================
  // GALERIA DE MÍDIAS (IA)
  // ==========================================
  media_library: {
    code: 'media_library',
    name: 'Galeria de Mídias (IA)',
    shortName: 'Galeria IA',
    icon: 'IconPhoto',
    emoji: '🖼️',
    category: 'core',
    description: 'A IA decide quando enviar mídias da galeria ao cliente',
    longDescription: 'Configure instruções por mídia e a IA envia automaticamente imagens, PDFs ou áudios quando a conversa indicar.',
    configFields: [],  // UI custom no ConfigPanel
  },

  // ==========================================
  // ANÁLISE DE MÍDIAS
  // ==========================================
  media_analysis: {
    code: 'media_analysis',
    name: 'Análise de Mídias',
    shortName: 'Análise',
    icon: 'IconEye',
    emoji: '🔍',
    category: 'core',
    description: 'Analisar imagens e documentos enviados pelo cliente',
    longDescription: 'O agente analisa imagens e documentos PDF enviados pelo cliente usando IA. Ideal para validar comprovantes, receitas médicas, notas fiscais e muito mais.',
    configFields: [
      {
        key: 'prompt_preset',
        label: 'Tipo de análise',
        type: 'select',
        options: ['custom','payment_receipt','medical_prescription','medical_exam','invoice',
                  'identity_document','vehicle_document','contract','property_document','product_photo','general'],
        optionLabels: {
          custom: '✏️ Personalizado',
          payment_receipt: '💳 Comprovante de Pagamento',
          medical_prescription: '💊 Receita Médica',
          medical_exam: '🔬 Resultado de Exame',
          invoice: '🧾 Nota Fiscal',
          identity_document: '🪪 Documento de Identidade',
          vehicle_document: '🚗 Documento de Veículo',
          contract: '📑 Contrato',
          property_document: '🏠 Documento de Imóvel',
          product_photo: '📦 Foto de Produto',
          general: '📋 Análise Geral',
        },
        default: 'general',
        tooltip: 'Selecione um modelo pré-configurado. O texto será inserido no campo abaixo para personalização.',
      },
      {
        key: 'prompt_template',
        label: 'Prompt de análise (o que extrair)',
        type: 'textarea',
        default: '',
        placeholder: 'Descreva o que a IA deve analisar e extrair do conteúdo...',
        tooltip: 'Instruções para a IA sobre O QUE analisar e extrair do conteúdo enviado pelo cliente.',
      },
      {
        key: 'response_prompt',
        label: 'Instruções de resposta (o que fazer com o resultado)',
        type: 'textarea',
        default: '',
        placeholder: 'Ex: Se o comprovante indica pagamento realizado com sucesso, confirme amigavelmente ao cliente e pergunte se quer finalizar o agendamento...',
        tooltip: 'Instruções para a IA sobre COMO responder ao cliente após a análise. Defina o que fazer com o resultado da extração. Se vazio, a IA responde diretamente com o resultado da análise.',
      },
      { key: '_section_config', type: 'section', label: 'Configurações' },
      {
        key: 'require_media',
        label: 'Aguardar envio de mídia',
        type: 'boolean',
        default: true,
        tooltip: 'Se ativado, o fluxo será pausado até que o cliente envie uma imagem ou documento. Ideal quando a mídia é obrigatória (ex: comprovante de pagamento).',
      },
      {
        key: 'detail_level',
        label: 'Nível de detalhe',
        type: 'select',
        options: ['high', 'low'],
        optionLabels: { high: 'Alto (detalhado)', low: 'Baixo (resumido)' },
        default: 'high',
        tooltip: 'Alto: análise completa. Baixo: apenas pontos essenciais.',
      },
      {
        key: 'max_pdf_pages',
        label: 'Máximo de páginas (PDF)',
        type: 'number', default: 1, min: 1, max: 3,
        tooltip: 'Número máximo de páginas analisadas em documentos PDF.',
      },
      {
        key: 'no_media_message',
        label: 'Como a IA deve pedir a mídia',
        type: 'textarea',
        default: 'Peça educadamente para o cliente enviar a imagem ou documento.',
        placeholder: 'Ex: Solicite o envio do comprovante de pagamento para prosseguir com o agendamento...',
        tooltip: 'Diretiva para a IA — NÃO escreva o texto literal que vai ao cliente. A IA segue esta instrução e gera uma mensagem natural. Ex: escreva "peça educadamente o comprovante do PIX" (a IA vai reformular para o cliente), e não "Estou aguardando o comprovante 💜" (esse texto poderia ser copiado direto).',
        showIf: { require_media: true },
      },
      {
        key: 'fallback_when_no_media',
        label: 'Quando cliente faz pergunta sem enviar mídia',
        type: 'select',
        options: ['fixed_message', 'faq'],
        optionLabels: {
          fixed_message: 'Apenas reforçar pedido (modo seguro)',
          faq: 'Responder via base de conhecimento + reforçar pedido',
        },
        default: 'fixed_message',
        tooltip: 'No modo "Apenas reforçar pedido", o agente apenas reafirma o pedido de mídia, ignorando qualquer pergunta. No modo "Base de conhecimento", o agente busca a resposta na base e em seguida reforça o pedido. Use este último apenas se o agente tiver base de conhecimento configurada — caso contrário, sempre responderá "não tenho essa informação".',
        showIf: { require_media: true },
      },
    ],
  },

  sheets_reader: {
    code: 'sheets_reader',
    name: 'Leitor de Planilha',
    shortName: 'Planilha',
    icon: 'IconFileSpreadsheet',
    emoji: '📊',
    category: 'integration',
    description: 'Analisa e conversa sobre dados de planilhas Google Sheets',
    longDescription: 'Quando a conversa chega neste nó, o agente lê dados de uma planilha Google Sheets configurada, analisa conforme instruções e conversa com o usuário sobre os dados. Pode enviar resumos para contatos via WhatsApp.',
    requiresIntegration: 'google_workspace',
    configFields: [
      { key: '_section_planilha', type: 'section', label: 'Configuração da Planilha' },
      {
        key: 'spreadsheet_id',
        label: 'Planilha',
        type: 'spreadsheet_picker',
        required: true,
        tooltip: 'Selecione a planilha Google Sheets que o agente deve analisar.',
      },
      {
        key: 'sheet_name',
        label: 'Aba da Planilha',
        type: 'sheet_tab_picker',
        required: true,
        tooltip: 'Selecione qual aba da planilha será lida.',
      },
      { key: '_section_analise', type: 'section', label: 'Análise' },
      {
        key: 'analysis_instructions',
        label: 'Instruções de análise',
        type: 'textarea',
        default: '',
        placeholder: 'Ex: Analise os dados de vendas e identifique os top 10 clientes que compraram acima de R$100mil neste mês...',
        tooltip: 'Instruções que a IA seguirá ao analisar os dados da planilha. Quanto mais específico, melhor o resultado.',
      },
    ],
  },

  // ==========================================
  // GOOGLE WORKSPACE CAPABILITIES
  // ==========================================
  google_calendar_sync: {
    code: 'google_calendar_sync',
    name: 'Sincronização Google Calendar',
    shortName: 'Google Calendar',
    icon: 'IconCalendar',
    emoji: '📆',
    category: 'integration',
    description: 'Sincroniza agendamentos automaticamente com Google Calendar',
    longDescription: 'Quando um agendamento é criado, cancelado ou remarcado, o evento é automaticamente sincronizado com o Google Calendar do profissional.',
    requiresIntegration: 'google_workspace',
    configFields: [
      { key: 'auto_sync', label: 'Sincronizar automaticamente?', type: 'boolean', default: true, tooltip: 'Sincroniza eventos automaticamente ao criar/cancelar/remarcar agendamentos.' },
      // { key: 'calendar_id', label: 'ID do Calendário', type: 'text', default: 'primary', tooltip: 'Use "primary" para o calendário principal ou o ID específico.' },
      { key: 'sync_new_appointments', label: 'Sincronizar novos agendamentos?', type: 'boolean', default: true },
      { key: 'sync_cancellations', label: 'Sincronizar cancelamentos?', type: 'boolean', default: true },
      { key: 'send_invites', label: 'Enviar convites por email?', type: 'boolean', default: false, tooltip: 'Envia convite por email para o cliente.' },
    ],
  },
  gmail_assistant: {
    code: 'gmail_assistant',
    name: 'Assistente Gmail',
    shortName: 'Gmail',
    icon: 'IconMail',
    emoji: '📧',
    category: 'integration',
    description: 'Busca, lê, envia e responde emails via Gmail',
    longDescription: 'O agente pode interagir com a caixa de entrada do Gmail, buscando emails, lendo conteúdo, enviando novos emails ou respondendo threads existentes.',
    requiresIntegration: 'google_workspace',
    configFields: [
      { key: 'can_send', label: 'Pode enviar emails?', type: 'boolean', default: true },
      { key: 'can_reply', label: 'Pode responder emails?', type: 'boolean', default: true },
      { key: 'require_approval', label: 'Requer aprovação para envio?', type: 'boolean', default: true, tooltip: 'Quando ativo, o agente pede confirmação antes de enviar emails.' },
      { key: 'signature', label: 'Assinatura', type: 'textarea', default: '', tooltip: 'Assinatura adicionada ao final de cada email enviado.' },
    ],
  },
  gdrive_assistant: {
    code: 'gdrive_assistant',
    name: 'Assistente Google Drive',
    shortName: 'Drive',
    icon: 'IconFolder',
    emoji: '📁',
    category: 'integration',
    description: 'Lista, lê, cria e organiza arquivos no Google Drive',
    longDescription: 'O agente pode navegar pelo Google Drive, listar arquivos, ler conteúdo de documentos, fazer upload de novos arquivos e criar pastas.',
    requiresIntegration: 'google_workspace',
    configFields: [
      { key: 'can_upload', label: 'Pode fazer upload?', type: 'boolean', default: true },
      { key: 'can_create_folders', label: 'Pode criar pastas?', type: 'boolean', default: true },
    ],
  },
  // gdocs_assistant: {
  //   code: 'gdocs_assistant',
  //   name: 'Assistente Google Docs',
  //   shortName: 'Docs',
  //   icon: 'IconFileText',
  //   emoji: '📄',
  //   category: 'integration',
  //   description: 'Lê, cria e edita documentos no Google Docs',
  //   longDescription: 'O agente pode ler conteúdo de documentos existentes, criar novos documentos e adicionar texto a documentos.',
  //   requiresIntegration: 'google_workspace',
  //   configFields: [
  //     { key: 'can_create', label: 'Pode criar documentos?', type: 'boolean', default: true },
  //     { key: 'can_edit', label: 'Pode editar documentos?', type: 'boolean', default: true },
  //   ],
  // },
  email_monitor: {
    code: 'email_monitor',
    name: 'Leitor de Email',
    shortName: 'Leitor Email',
    icon: 'IconMailSearch',
    emoji: '📧',
    category: 'integration',
    description: 'Lê, analisa e age sobre emails do Gmail quando o fluxo chega neste nó',
    longDescription: 'Quando a conversa chega neste nó, o agente busca emails recentes no Gmail com filtros configuráveis (período, remetente, assunto), analisa o conteúdo com IA e pode responder emails, encaminhar resumos via WhatsApp ou marcar como lido.',
    requiresIntegration: 'google_workspace',
    configFields: [
      { key: '_section_filters', type: 'section', label: 'Filtros de Email' },
      {
        key: 'time_filter',
        label: 'Período de busca',
        type: 'select',
        options: ['24h'],
        optionLabels: { '24h': 'Últimas 24 horas', '48h': 'Últimas 48 horas', '72h': 'Últimas 72 horas', '7d': 'Últimos 7 dias' },
        default: '24h',
        tooltip: 'Define o período de tempo para buscar emails.',
      },
      {
        key: 'unread_only',
        label: 'Apenas não lidos',
        type: 'boolean',
        default: true,
        tooltip: 'Quando ativo, busca apenas emails que ainda não foram lidos.',
      },
      {
        key: 'label_filter',
        label: 'Caixa de email',
        type: 'select',
        options: ['all', 'primary', 'promotions', 'social', 'updates', 'forums'],
        optionLabels: {
          all: 'Todas as caixas',
          primary: 'Principal',
          promotions: 'Promoções',
          social: 'Social',
          updates: 'Atualizações',
          forums: 'Fóruns',
        },
        default: 'all',
        tooltip: 'Selecione de qual caixa/categoria do Gmail buscar os emails.',
      },
      {
        key: 'sender_filter',
        label: 'Filtrar por remetente(s)',
        type: 'text',
        default: '',
        placeholder: 'Ex: cliente@email.com, fornecedor@email.com',
        tooltip: 'Emails separados por vírgula. Deixe vazio para buscar de todos os remetentes.',
      },
      {
        key: 'subject_filter',
        label: 'Filtrar por assunto',
        type: 'text',
        default: '',
        placeholder: 'Ex: orçamento, pedido, fatura',
        tooltip: 'Busca parcial no assunto do email. Deixe vazio para qualquer assunto.',
      },

      { key: '_section_reply', type: 'section', label: 'Resposta Automática' },
      {
        key: 'auto_reply',
        label: 'Responder emails automaticamente',
        type: 'boolean',
        default: false,
        tooltip: 'Quando ativo, a IA pode responder emails com base nas instruções abaixo.',
      },
      {
        key: 'reply_instructions',
        label: 'Instruções para resposta',
        type: 'textarea',
        default: '',
        placeholder: 'Ex: Responda confirmando o recebimento e informe que retornaremos em até 24h...',
        tooltip: 'Instruções que a IA seguirá ao responder emails. Seja específico sobre tom, conteúdo e quando responder.',
        showIf: { auto_reply: true },
      },

      { key: '_section_whatsapp', type: 'section', label: 'Encaminhar via WhatsApp' },
      {
        key: 'forward_to_whatsapp',
        label: 'Enviar resumo via WhatsApp',
        type: 'boolean',
        default: false,
        tooltip: 'Envia um resumo dos emails encontrados para um número de WhatsApp.',
      },
      {
        key: 'whatsapp_number',
        type: 'phone',
        showIf: { forward_to_whatsapp: true },
      },
      {
        key: 'whatsapp_summary',
        label: 'Resumo consolidado',
        type: 'boolean',
        default: true,
        tooltip: 'Ativo: envia um único resumo com todos os emails. Desativado: envia uma notificação para cada email.',
        showIf: { forward_to_whatsapp: true },
      },

      { key: '_section_post', type: 'section', label: 'Pós-processamento' },
      {
        key: 'mark_as_read',
        label: 'Marcar como lido após processar',
        type: 'boolean',
        default: false,
        tooltip: 'Marca os emails processados como lidos no Gmail.',
      },
    ],
  },
  gsheets_assistant: {
    code: 'gsheets_assistant',
    name: 'Google Sheets (CRUD)',
    shortName: 'Sheets',
    icon: 'IconFileSpreadsheet',
    emoji: '📊',
    category: 'integration',
    description: 'Lê e grava dados em planilhas Google Sheets',
    longDescription: 'O agente pode ler, adicionar, atualizar e deletar dados em planilhas do Google Sheets. Ideal para captura de leads, cadastros e gestão de dados coletados do cliente.',
    requiresIntegration: 'google_sheets',
    configFields: [
      { key: 'spreadsheet_id', label: 'ID da Planilha', type: 'text', required: true, tooltip: 'ID encontrado na URL da planilha Google Sheets (entre /d/ e /edit)' },
      { key: 'sheet_name', label: 'Nome da Aba', type: 'text', default: 'Sheet1', tooltip: 'Nome da aba principal onde os dados serão gravados' },
      { key: 'auto_save_leads', label: 'Salvar leads automaticamente?', type: 'boolean', default: true, tooltip: 'Quando ativo, grava automaticamente dados coletados do cliente na planilha' },
    ],
  },
};

// ===============================================
// FAQ BEHAVIOR TEMPLATES
// ===============================================

export const FAQ_BEHAVIOR_TEMPLATES = {
  general: `Você está respondendo dúvidas do cliente de forma profissional e cordial.
- Consulte a base de conhecimento antes de responder qualquer pergunta
- Seja conciso e direto nas respostas, sem rodeios
- Quando houver múltiplas informações relevantes, organize em tópicos
- Mantenha um tom acolhedor e prestativo
- Se não encontrar a resposta na base de conhecimento, ofereça transferir para um atendente humano`,

  clinic: `Você está respondendo dúvidas de pacientes/clientes de uma clínica ou consultório.
- Consulte a base de conhecimento para informações sobre procedimentos, preparos e cuidados
- Seja empático e acolhedor — muitos pacientes podem estar ansiosos ou preocupados
- NUNCA forneça diagnósticos, prescrições ou orientações médicas específicas
- Para dúvidas clínicas específicas, oriente o paciente a consultar o profissional de saúde
- Informe sobre preparos pré-consulta e cuidados pós-procedimento quando disponível na base
- Se não encontrar a resposta na base de conhecimento, ofereça transferir para um atendente humano`,

  beauty: `Você está respondendo dúvidas de clientes de um salão, barbearia ou clínica estética.
- Consulte a base de conhecimento para informações sobre serviços, profissionais e cuidados
- Seja simpático e entusiasmado ao falar sobre os serviços oferecidos
- Informe sobre cuidados pré e pós-procedimento quando disponível na base
- Destaque diferenciais do profissional ou do estabelecimento quando relevante
- Para dúvidas sobre contraindicações, oriente consultar o profissional antes do procedimento
- Se não encontrar a resposta na base de conhecimento, ofereça transferir para um atendente humano`,

  restaurant: `Você está respondendo dúvidas de clientes de um restaurante, lanchonete ou delivery.
- Consulte a base de conhecimento para informações sobre cardápio, ingredientes e funcionamento
- Informe sobre alérgenos e restrições alimentares com atenção especial quando perguntado
- Destaque pratos populares, promoções e combos quando relevante
- Seja claro sobre formas de pagamento, delivery e tempo de entrega quando disponível
- Para pedidos específicos ou alterações no cardápio, oriente falar com a equipe
- Se não encontrar a resposta na base de conhecimento, ofereça transferir para um atendente humano`,

  ecommerce: `Você está respondendo dúvidas de clientes de uma loja ou e-commerce.
- Consulte a base de conhecimento para informações sobre produtos, preços e políticas
- Informe claramente sobre prazos de entrega, formas de pagamento e política de troca/devolução
- Destaque características e benefícios dos produtos quando perguntado
- Para problemas com pedidos existentes, colete número do pedido e direcione ao suporte
- Seja objetivo com informações de estoque e disponibilidade quando disponível
- Se não encontrar a resposta na base de conhecimento, ofereça transferir para um atendente humano`,

  realestate: `Você está respondendo dúvidas de clientes de uma imobiliária ou construtora.
- Consulte a base de conhecimento para informações sobre imóveis, valores e documentação
- Apresente características dos imóveis de forma clara: metragem, quartos, localização, valor
- Informe sobre documentação necessária e etapas do processo de compra/aluguel
- Para valores e condições de pagamento, forneça apenas informações disponíveis na base
- Para negociações ou visitas, direcione para o corretor responsável
- Se não encontrar a resposta na base de conhecimento, ofereça transferir para um atendente humano`,

  education: `Você está respondendo dúvidas de alunos ou interessados em uma instituição de ensino.
- Consulte a base de conhecimento para informações sobre cursos, matrículas e certificações
- Informe sobre grade curricular, carga horária e pré-requisitos quando disponível
- Esclareça sobre valores, formas de pagamento e possíveis bolsas/descontos
- Para dúvidas acadêmicas específicas, oriente consultar a coordenação do curso
- Destaque diferenciais da metodologia e infraestrutura quando relevante
- Se não encontrar a resposta na base de conhecimento, ofereça transferir para um atendente humano`,

  legal: `Você está respondendo dúvidas de clientes de um escritório de advocacia ou consultoria jurídica.
- Consulte a base de conhecimento para informações sobre áreas de atuação e procedimentos
- NUNCA forneça parecer jurídico, interpretação de leis ou aconselhamento legal específico
- Informe sobre documentação necessária e etapas de processos quando disponível na base
- Esclareça sobre formas de atendimento, honorários e prazos gerais
- Para consultas específicas sobre casos, oriente agendar uma consulta com o advogado
- Se não encontrar a resposta na base de conhecimento, ofereça transferir para um atendente humano`,

  support: `Você está respondendo dúvidas de suporte técnico ou atendimento ao cliente.
- Consulte a base de conhecimento para soluções de problemas conhecidos e tutoriais
- Forneça instruções passo a passo de forma clara e numerada
- Peça informações específicas do problema: mensagens de erro, versão do produto, etc.
- Para problemas não documentados ou complexos, escale para o time técnico
- Confirme se a solução funcionou antes de encerrar o atendimento
- Se não encontrar a resposta na base de conhecimento, ofereça transferir para um atendente humano`,

  sales: `Você está respondendo dúvidas com foco em conversão de vendas.
- Consulte a base de conhecimento para informações sobre produtos/serviços e diferenciais
- Destaque benefícios e valor agregado das soluções oferecidas
- Identifique a necessidade do cliente e sugira a melhor opção disponível
- Apresente casos de sucesso ou depoimentos quando disponível na base
- Para propostas personalizadas ou descontos especiais, direcione ao time comercial
- Se não encontrar a resposta na base de conhecimento, ofereça transferir para um atendente humano`,
};

// ===============================================
// CAPABILITY CATEGORIES
// ===============================================

export const CAPABILITY_CATEGORIES = [
  { id: 'core', name: 'Essenciais', description: 'Capacidades principais para atendimento', icon: 'IconStar' },
  { id: 'qualification', name: 'Qualificação', description: 'Qualificação e scoring de leads', icon: 'IconTarget' },
  { id: 'automation', name: 'Automação', description: 'Tarefas automatizadas e follow-up', icon: 'IconRobot' },
  { id: 'commerce', name: 'Vendas', description: 'Funcionalidades de e-commerce', icon: 'IconShoppingCart' },
  { id: 'integration', name: 'Integrações', description: 'Conexões com sistemas externos', icon: 'IconPlug' },
];

// ===============================================
// INTEGRATIONS CATALOG
// ===============================================

export const INTEGRATIONS = {
  // ==========================================
  // GOOGLE WORKSPACE
  // ==========================================
  // google_workspace: {
  //   code: 'google_workspace',
  //   name: 'Google Workspace',
  //   icon: 'IconBrandGoogle',
  //   description: 'Sheets, Calendar, Drive e Gmail',
  //   services: ['sheets', 'calendar', 'drive', 'gmail'],
  //   authType: 'oauth',
  //   category: 'productivity',
  //   configFields: [
  //     { key: 'sheets_enabled', label: 'Google Sheets', type: 'boolean', default: true },
  //     { key: 'calendar_enabled', label: 'Google Calendar', type: 'boolean', default: true },
  //     { key: 'drive_enabled', label: 'Google Drive', type: 'boolean', default: false },
  //     { key: 'gmail_enabled', label: 'Gmail', type: 'boolean', default: false },
  //   ],
  // },
  google_calendar: {
    code: 'google_calendar',
    name: 'Google Calendar',
    icon: 'IconCalendar',
    description: 'Agendamentos via Google Calendar',
    authType: 'oauth',
    category: 'productivity',
    // configFields removidos - configuração gerenciada pelo GoogleCalendarConfig component
    configFields: [],
  },
  gmail: {
    code: 'gmail',
    name: 'Gmail (Workspace)',
    icon: 'IconBrandGmail',
    description: 'Ler e enviar emails via Gmail (requer Google Workspace)',
    authType: 'service_account',
    category: 'communication',
    configFields: [],
  },
  google_drive: {
    code: 'google_drive',
    name: 'Google Drive',
    icon: 'IconBrandGoogleDrive',
    description: 'Arquivos e pastas do Google Drive',
    authType: 'oauth',
    category: 'productivity',
    // configFields: [
    //   { key: 'default_folder_id', label: 'Pasta padrão (ID)', type: 'text', default: '' },
    //   { key: 'can_upload', label: 'Permitir upload?', type: 'boolean', default: true },
    //   { key: 'can_create_folders', label: 'Criar pastas?', type: 'boolean', default: true },
    // ],
  },
  // google_docs: { ###DESATIVADO TEMPORARIAMENTE
  //   code: 'google_docs',
  //   name: 'Google Docs',
  //   icon: 'IconFileText',
  //   description: 'Criar e editar documentos Google Docs',
  //   authType: 'oauth',
  //   category: 'productivity',
  //   configFields: [
  //     { key: 'can_create', label: 'Criar documentos?', type: 'boolean', default: true },
  //     { key: 'can_edit', label: 'Editar documentos?', type: 'boolean', default: true },
  //   ],
  // },
  google_sheets: {
    code: 'google_sheets',
    name: 'Google Sheets',
    icon: 'IconFileSpreadsheet',
    description: 'Ler e gravar dados em planilhas Google',
    authType: 'oauth',
    category: 'productivity',
    configFields: [],
  },

  // ==========================================
  // SHIPPING INTEGRATIONS (DESABILITADO)
  // ==========================================
  // correios: {
  //   code: 'correios',
  //   name: 'Correios',
  //   icon: 'IconMail',
  //   description: 'Rastreamento e cotação de frete',
  //   authType: 'credentials',
  //   category: 'shipping',
  //   configFields: [
  //     { key: 'cep_origem', label: 'CEP de Origem', type: 'text', required: true },
  //     { key: 'contrato', label: 'Número do Contrato', type: 'text' },
  //     { key: 'senha', label: 'Senha do Contrato', type: 'password' },
  //     { key: 'servicos', label: 'Serviços Habilitados', type: 'multiselect', options: ['SEDEX', 'PAC', 'SEDEX 10', 'SEDEX 12', 'SEDEX HOJE'], default: ['SEDEX', 'PAC'] },
  //   ],
  // },
  // jadlog: {
  //   code: 'jadlog',
  //   name: 'JadLog',
  //   icon: 'IconTruck',
  //   description: 'Transportadora JadLog',
  //   authType: 'api_key',
  //   category: 'shipping',
  //   configFields: [
  //     { key: 'cnpj', label: 'CNPJ', type: 'text', required: true },
  //     { key: 'api_key', label: 'Token de API', type: 'password', required: true },
  //     { key: 'modalidade', label: 'Modalidade', type: 'select', options: ['EXPRESSO', 'RODOVIÁRIO', 'PACKAGE'], default: 'RODOVIÁRIO' },
  //     { key: 'cep_origem', label: 'CEP de Origem', type: 'text', required: true },
  //   ],
  // },
  // melhor_envio: {
  //   code: 'melhor_envio',
  //   name: 'Melhor Envio',
  //   icon: 'IconPackage',
  //   description: 'Agregador de transportadoras',
  //   authType: 'oauth',
  //   category: 'shipping',
  //   configFields: [
  //     { key: 'environment', label: 'Ambiente', type: 'select', options: ['sandbox', 'production'], default: 'sandbox' },
  //     { key: 'auto_shipping', label: 'Gerar etiqueta automaticamente?', type: 'boolean', default: false },
  //   ],
  // },
  // loggi: {
  //   code: 'loggi',
  //   name: 'Loggi',
  //   icon: 'IconBike',
  //   description: 'Entregas rápidas',
  //   authType: 'api_key',
  //   category: 'shipping',
  //   configFields: [
  //     { key: 'api_key', label: 'API Key', type: 'password', required: true },
  //     { key: 'company_id', label: 'Company ID', type: 'text', required: true },
  //   ],
  // },

  // ==========================================
  // ADDITIONAL COMMUNICATION (DESABILITADO)
  // ==========================================
  // sms_zenvia: {
  //   code: 'sms_zenvia',
  //   name: 'Zenvia SMS',
  //   icon: 'IconDeviceMobile',
  //   description: 'Envio de SMS via Zenvia',
  //   authType: 'api_key',
  //   category: 'communication',
  //   configFields: [
  //     { key: 'api_key', label: 'API Key', type: 'password', required: true },
  //     { key: 'from', label: 'Remetente', type: 'text', required: true },
  //   ],
  // },

  // ==========================================
  // NOTION (DESABILITADO)
  // ==========================================
  // notion: {
  //   code: 'notion',
  //   name: 'Notion',
  //   icon: 'IconBrandNotion',
  //   description: 'Sincronizar bases do Notion',
  //   authType: 'oauth',
  //   category: 'productivity',
  //   configFields: [
  //     { key: 'database_id', label: 'ID do Database', type: 'text' },
  //     { key: 'sync_frequency', label: 'Frequência de Sync', type: 'select', options: ['manual', 'hourly', 'daily'], default: 'daily' },
  //     { key: 'include_children', label: 'Incluir páginas filhas?', type: 'boolean', default: true },
  //   ],
  // },

  // ==========================================
  // CRMs
  // ==========================================
  rdstation: {
    code: 'rdstation',
    name: 'RD Station CRM',
    icon: 'IconChartBar',
    description: 'CRM e automação de vendas',
    authType: 'api_key',
    category: 'crm',
    configFields: [
      { key: 'api_token', label: 'Token da API', type: 'password', required: true, placeholder: 'Cole aqui o token da API do RD Station CRM' },
    ],
  },
  // hubspot: {
  //   code: 'hubspot',
  //   name: 'HubSpot',
  //   icon: 'IconBrandHubspot',
  //   description: 'CRM completo com marketing e vendas',
  //   authType: 'oauth',
  //   category: 'crm',
  //   configFields: [
  //     { key: 'sync_contacts', label: 'Sincronizar contatos?', type: 'boolean', default: true },
  //     { key: 'sync_deals', label: 'Sincronizar negócios?', type: 'boolean', default: true },
  //     { key: 'sync_tickets', label: 'Sincronizar tickets?', type: 'boolean', default: false },
  //     { key: 'auto_create_contact', label: 'Criar contato automaticamente?', type: 'boolean', default: true },
  //     { key: 'default_pipeline', label: 'Pipeline padrão', type: 'text', default: '' },
  //   ],
  // },
  // pipedrive: {
  //   code: 'pipedrive',
  //   name: 'Pipedrive',
  //   icon: 'IconPipeline',
  //   description: 'CRM focado em vendas',
  //   authType: 'api_key',
  //   category: 'crm',
  //   configFields: [
  //     { key: 'api_key', label: 'API Token', type: 'password', required: true },
  //     { key: 'sync_persons', label: 'Sincronizar pessoas?', type: 'boolean', default: true },
  //     { key: 'sync_deals', label: 'Sincronizar negócios?', type: 'boolean', default: true },
  //     { key: 'auto_create_person', label: 'Criar pessoa automaticamente?', type: 'boolean', default: true },
  //     { key: 'default_pipeline_id', label: 'ID do Pipeline padrão', type: 'text', default: '' },
  //   ],
  // },
  // salesforce: {
  //   code: 'salesforce',
  //   name: 'Salesforce',
  //   icon: 'IconCloud',
  //   description: 'CRM enterprise completo',
  //   authType: 'oauth',
  //   category: 'crm',
  //   configFields: [
  //     { key: 'sync_leads', label: 'Sincronizar leads?', type: 'boolean', default: true },
  //     { key: 'sync_contacts', label: 'Sincronizar contatos?', type: 'boolean', default: true },
  //     { key: 'sync_opportunities', label: 'Sincronizar oportunidades?', type: 'boolean', default: true },
  //     { key: 'auto_create_lead', label: 'Criar lead automaticamente?', type: 'boolean', default: true },
  //     { key: 'lead_source', label: 'Origem do Lead', type: 'text', default: 'WhatsApp Agent' },
  //   ],
  // },

  // ==========================================
  // SUPPORT (DESABILITADO)
  // ==========================================
  // zendesk: {
  //   code: 'zendesk',
  //   name: 'Zendesk',
  //   icon: 'IconHeadset',
  //   description: 'Suporte ao cliente',
  //   authType: 'api_key',
  //   category: 'support',
  //   configFields: [
  //     { key: 'subdomain', label: 'Subdomínio', type: 'text', required: true, placeholder: 'empresa.zendesk.com' },
  //     { key: 'api_key', label: 'API Token', type: 'password', required: true },
  //     { key: 'email', label: 'Email do usuário', type: 'text', required: true },
  //     { key: 'auto_create_ticket', label: 'Criar ticket automaticamente?', type: 'boolean', default: true },
  //     { key: 'default_priority', label: 'Prioridade padrão', type: 'select', options: ['low', 'normal', 'high', 'urgent'], default: 'normal' },
  //   ],
  // },
  // freshdesk: {
  //   code: 'freshdesk',
  //   name: 'Freshdesk',
  //   icon: 'IconLifebuoy',
  //   description: 'Help desk e suporte',
  //   authType: 'api_key',
  //   category: 'support',
  //   configFields: [
  //     { key: 'domain', label: 'Domínio', type: 'text', required: true, placeholder: 'empresa.freshdesk.com' },
  //     { key: 'api_key', label: 'API Key', type: 'password', required: true },
  //     { key: 'auto_create_ticket', label: 'Criar ticket automaticamente?', type: 'boolean', default: true },
  //     { key: 'default_priority', label: 'Prioridade padrão', type: 'select', options: ['1', '2', '3', '4'], default: '2' },
  //   ],
  // },
  // intercom: {
  //   code: 'intercom',
  //   name: 'Intercom',
  //   icon: 'IconMessages',
  //   description: 'Mensagens e suporte in-app',
  //   authType: 'api_key',
  //   category: 'support',
  //   configFields: [
  //     { key: 'access_token', label: 'Access Token', type: 'password', required: true },
  //     { key: 'sync_users', label: 'Sincronizar usuários?', type: 'boolean', default: true },
  //     { key: 'sync_conversations', label: 'Sincronizar conversas?', type: 'boolean', default: false },
  //   ],
  // },

  // ==========================================
  // COMMUNICATION (DESABILITADO)
  // ==========================================
  // email_smtp: {
  //   code: 'email_smtp',
  //   name: 'Email SMTP',
  //   icon: 'IconMail',
  //   description: 'Envio de emails via SMTP',
  //   authType: 'credentials',
  //   category: 'communication',
  //   configFields: [
  //     { key: 'host', label: 'Servidor SMTP', type: 'text', required: true },
  //     { key: 'port', label: 'Porta', type: 'number', default: 587 },
  //     { key: 'username', label: 'Usuário', type: 'text', required: true },
  //     { key: 'password', label: 'Senha', type: 'password', required: true },
  //     { key: 'from_email', label: 'Email de origem', type: 'text', required: true },
  //     { key: 'from_name', label: 'Nome de origem', type: 'text', default: '' },
  //     { key: 'use_tls', label: 'Usar TLS?', type: 'boolean', default: true },
  //   ],
  // },
  // sendgrid: {
  //   code: 'sendgrid',
  //   name: 'SendGrid',
  //   icon: 'IconSend',
  //   description: 'Envio de emails transacionais',
  //   authType: 'api_key',
  //   category: 'communication',
  //   configFields: [
  //     { key: 'api_key', label: 'API Key', type: 'password', required: true },
  //     { key: 'from_email', label: 'Email de origem', type: 'text', required: true },
  //     { key: 'from_name', label: 'Nome de origem', type: 'text', default: '' },
  //   ],
  // },
  // twilio: {
  //   code: 'twilio',
  //   name: 'Twilio SMS',
  //   icon: 'IconDeviceMobile',
  //   description: 'Envio de SMS via Twilio',
  //   authType: 'api_key',
  //   category: 'communication',
  //   configFields: [
  //     { key: 'account_sid', label: 'Account SID', type: 'text', required: true },
  //     { key: 'auth_token', label: 'Auth Token', type: 'password', required: true },
  //     { key: 'from_number', label: 'Número de origem', type: 'text', required: true, placeholder: '+5511999999999' },
  //   ],
  // },

  // ==========================================
  // AUTOMATION (DESABILITADO)
  // ==========================================
  zapier: {
    code: 'zapier',
    name: 'Zapier',
    icon: 'IconBolt',
    description: 'Envie dados para 5.000+ apps automaticamente',
    authType: 'custom',
    category: 'automation',
    configFields: [
      {
        key: 'webhook_url',
        label: 'URL do Webhook (Catch Hook)',
        type: 'text',
        required: true,
        placeholder: 'https://hooks.zapier.com/hooks/catch/...',
      },
      {
        key: 'trigger_events',
        label: 'Eventos que disparam o Zap',
        type: 'multiselect',
        options: [
          'lead_sent_to_crm',
          'appointment_created',
          'appointment_canceled',
          'appointment_rescheduled',
        ],
        default: ['lead_sent_to_crm'],
        labels: {
          lead_sent_to_crm: 'Dados capturados (Lead)',
          appointment_created: 'Agendamento criado',
          appointment_canceled: 'Agendamento cancelado',
          appointment_rescheduled: 'Agendamento remarcado',
        },
      },
    ],
  },
  make: {
    code: 'make',
    name: 'Make',
    icon: 'IconTool',
    description: 'Automação visual avançada com 1.800+ apps',
    authType: 'custom',
    category: 'automation',
    configFields: [
      {
        key: 'webhook_url',
        label: 'URL do Webhook (Custom Webhook)',
        type: 'text',
        required: true,
        placeholder: 'https://hook.us1.make.com/...',
      },
      {
        key: 'trigger_events',
        label: 'Eventos que disparam o cenário',
        type: 'multiselect',
        options: [
          'lead_sent_to_crm',
          'appointment_created',
          'appointment_canceled',
          'appointment_rescheduled',
        ],
        default: ['lead_sent_to_crm'],
        labels: {
          lead_sent_to_crm: 'Dados capturados (Lead)',
          appointment_created: 'Agendamento criado',
          appointment_canceled: 'Agendamento cancelado',
          appointment_rescheduled: 'Agendamento remarcado',
        },
      },
    ],
  },

  // ==========================================
  // CUSTOM
  // ==========================================
  webhook: {
    code: 'webhook',
    name: 'Webhook Customizado',
    icon: 'IconWebhook',
    description: 'Integre com qualquer sistema via HTTP',
    authType: 'custom',
    category: 'custom',
    configFields: [
      { key: 'url', label: 'URL do Webhook', type: 'text', required: true },
      { key: 'method', label: 'Método HTTP', type: 'select', options: ['POST', 'PUT', 'PATCH'], default: 'POST' },
      { key: 'headers', label: 'Headers (JSON)', type: 'textarea', default: '{}' },
      { key: 'auth_type', label: 'Tipo de autenticação', type: 'select', options: ['none', 'bearer', 'basic', 'api_key'], default: 'none' },
      { key: 'auth_value', label: 'Token/Credenciais', type: 'password', default: '' },
      { key: 'trigger_events', label: 'Eventos que disparam', type: 'multiselect', options: [
        'appointment_created',
        'appointment_canceled',
        'appointment_rescheduled',
        'lead_sent_to_crm',
      ], default: ['appointment_created', 'lead_sent_to_crm'],
        labels: {
          appointment_created: 'Agendamento criado',
          appointment_canceled: 'Agendamento cancelado',
          appointment_rescheduled: 'Agendamento remarcado',
          lead_sent_to_crm: 'Dados capturados (Lead)',
        }
      },
    ],
  },
  // rest_api: {
  //   code: 'rest_api',
  //   name: 'API REST Externa',
  //   icon: 'IconApi',
  //   description: 'Consumir APIs REST externas',
  //   authType: 'custom',
  //   category: 'custom',
  //   configFields: [
  //     { key: 'base_url', label: 'URL Base da API', type: 'text', required: true },
  //     { key: 'auth_type', label: 'Tipo de autenticação', type: 'select', options: ['none', 'bearer', 'basic', 'api_key'], default: 'none' },
  //     { key: 'auth_header', label: 'Header de autenticação', type: 'text', default: 'Authorization' },
  //     { key: 'auth_value', label: 'Token/Credenciais', type: 'password', default: '' },
  //     { key: 'timeout', label: 'Timeout (segundos)', type: 'number', default: 30 },
  //   ],
  // },
};

// ===============================================
// INTEGRATION CATEGORIES
// ===============================================

export const INTEGRATION_CATEGORIES = [
  { id: 'productivity', name: 'Google Workspace', description: 'Calendar, Drive, Gmail e Docs', icon: 'IconBrandGoogle' },
  { id: 'crm', name: 'CRM', description: 'Gestão de relacionamento', icon: 'IconUsers' },
  { id: 'automation', name: 'Automação', description: 'Zapier e similares', icon: 'IconBolt' },
  { id: 'custom', name: 'Personalizado', description: 'Webhooks e APIs', icon: 'IconCode' },
  // { id: 'support', name: 'Suporte', description: 'Help desk e tickets', icon: 'IconHeadset' },
  // { id: 'communication', name: 'Comunicação', description: 'Email e SMS', icon: 'IconMessage' },
  // { id: 'shipping', name: 'Frete', description: 'Transportadoras e entregas', icon: 'IconTruck' },
];

// ===============================================
// IDENTITY CONFIG OPTIONS
// ===============================================

export const TONE_OPTIONS = [
  { id: 'formal', name: 'Formal', description: 'Linguagem formal e corporativa' },
  { id: 'professional', name: 'Profissional', description: 'Equilíbrio entre formal e amigável' },
  { id: 'friendly', name: 'Amigável', description: 'Comunicação próxima e calorosa' },
  { id: 'casual', name: 'Casual', description: 'Informal e descontraído' },
];

export const GENDER_OPTIONS = [
  { id: 'male', name: 'Masculino', description: 'Identidade masculina' },
  { id: 'female', name: 'Feminino', description: 'Identidade feminina' },
  { id: 'neutral', name: 'Neutro', description: 'Gênero não especificado' },
];

// ===============================================
// BEHAVIOR CONFIG OPTIONS
// ===============================================

export const TRANSFER_CONDITIONS = [
  { id: 'client_request', name: 'Solicitação do Cliente', description: 'Quando o cliente pede explicitamente para falar com um humano' },
  { id: 'complaint', name: 'Reclamação', description: 'Quando o cliente está insatisfeito ou faz uma reclamação' },
  { id: 'out_of_scope', name: 'Fora do Escopo', description: 'Quando o assunto está fora das capacidades do agente' },
  // DESATIVADO — "Máximo de Tentativas" não é determinístico no backend.
  // O valor é injetado como instrução textual no system prompt do LLM, mas
  // não há contador real entre turnos — o LLM julga subjetivamente "houve N
  // tentativas sem sucesso". Para reativar, é preciso implementar contador
  // determinístico em Redis/state que incremente em sinais reais (cliente
  // repete pergunta, sentimento negativo, max_iterations, etc.).
  // { id: 'max_attempts', name: 'Máximo de Tentativas', description: 'Após várias tentativas sem sucesso' },
];

export const PAYMENT_METHODS = [
  { id: 'pix', name: 'PIX', icon: 'IconQrcode' },
  { id: 'credit_card', name: 'Cartão de Crédito', icon: 'IconCreditCard' },
  { id: 'debit_card', name: 'Cartão de Débito', icon: 'IconCreditCard' },
  { id: 'boleto', name: 'Boleto Bancário', icon: 'IconReceipt' },
  { id: 'cash', name: 'Dinheiro', icon: 'IconCash' },
  { id: 'transfer', name: 'Transferência', icon: 'IconArrowsExchange' },
];

export const BRAZILIAN_STATES = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
];

// ===============================================
// PERSONALITY TRAITS
// ===============================================

export const PERSONALITY_TRAITS = [
  { id: 'empathetic', name: 'Empático', description: 'Demonstra compreensão e cuidado' },
  { id: 'funny', name: 'Engraçado', description: 'Usa humor apropriado' },
  { id: 'objective', name: 'Objetivo', description: 'Vai direto ao ponto' },
  { id: 'technical', name: 'Técnico', description: 'Usa termos técnicos quando apropriado' },
  { id: 'welcoming', name: 'Acolhedor', description: 'Faz o cliente se sentir bem-vindo' },
  { id: 'direct', name: 'Direto', description: 'Comunicação clara e sem rodeios' },
  { id: 'enthusiastic', name: 'Entusiasta', description: 'Demonstra energia e entusiasmo' },
  { id: 'calm', name: 'Calmo', description: 'Transmite tranquilidade' },
  { id: 'persuasive', name: 'Persuasivo', description: 'Bom em convencer e vender' },
  { id: 'professional', name: 'Profissional', description: 'Postura corporativa e confiável' },
  { id: 'friendly', name: 'Simpático', description: 'Cria conexão rápida e agradável' },
  { id: 'helpful', name: 'Prestativo', description: 'Sempre disposto a ajudar' },
  { id: 'agile', name: 'Ágil', description: 'Respostas rápidas e eficientes' },
  { id: 'attentive', name: 'Atencioso', description: 'Presta atenção aos detalhes do cliente' },
  { id: 'organized', name: 'Organizado', description: 'Estruturado e metódico' },
  { id: 'reliable', name: 'Confiável', description: 'Transmite segurança e credibilidade' },
  { id: 'consultative', name: 'Consultivo', description: 'Abordagem investigativa e estratégica' },
  { id: 'knowledgeable', name: 'Conhecedor', description: 'Demonstra domínio do assunto' },
  { id: 'polite', name: 'Educado', description: 'Cordial e respeitoso' },
  { id: 'analytical', name: 'Analítico', description: 'Analisa dados e contexto com profundidade' },
];

// ===============================================
// DEFAULT MESSAGES
// ===============================================

export const DEFAULT_MESSAGES = {
  greeting: 'Olá! 👋 Sou {agent_name}, assistente virtual da {business_name}. Como posso ajudar você hoje?',
  not_understood: 'Desculpe, não consegui entender. Você poderia reformular sua pergunta? Posso ajudar com agendamentos, cancelamentos ou tirar dúvidas sobre nossos serviços.',
  scheduling_confirmation: 'Perfeito, {client_name}! ✅\n\nSua consulta está confirmada:\n📅 Data: {date}\n🕐 Horário: {time}\n👨‍⚕️ Profissional: {professional}\n\nAté lá! 😊',
  cancellation_confirmation: 'Seu agendamento foi cancelado com sucesso. Se precisar de algo mais, é só me chamar!',
  transfer_message: 'Entendi. Vou transferir você para um de nossos atendentes que poderá ajudar melhor. Um momento, por favor.',
  goodbye: 'Foi um prazer ajudar! Se precisar de algo mais, estou por aqui. Até logo! 👋',
};

// ===============================================
// EXPERIENCE LEVELS
// ===============================================

export const EXPERIENCE_LEVELS = {
  QUICK: 1,      // Level 1: Criação rápida
  CUSTOM: 2,     // Level 2: Personalizado
  ADVANCED: 3,   // Level 3: Avançado (Canvas)
};

// ===============================================
// WIZARD STEPS (Level 1)
// ===============================================

export const WIZARD_STEPS = [
  { id: 1, title: 'Tipo de Negócio', description: 'Escolha o segmento do seu negócio' },
  { id: 2, title: 'Dados do Negócio', description: 'Informações básicas' },
  { id: 3, title: 'Capacidades', description: 'O que seu agente faz' },
  { id: 4, title: 'Pronto!', description: 'Revise e ative' },
];

// ===============================================
// EDITOR TABS (Level 2)
// ===============================================

export const EDITOR_TABS = [
  { id: 'identity', name: 'Identidade', icon: 'IconUser' },
  { id: 'capabilities', name: 'Capacidades', icon: 'IconPuzzle' },
  { id: 'messages', name: 'Mensagens', icon: 'IconMessage' },
  { id: 'faq', name: 'FAQ', icon: 'IconQuestionMark' },
  { id: 'behavior', name: 'Comportamento', icon: 'IconAdjustments' },
  { id: 'integrations', name: 'Integrações', icon: 'IconPlug' },
  { id: 'knowledge', name: 'Conhecimento', icon: 'IconBook' },
  { id: 'notifications', name: 'Notificações', icon: 'IconBell' },
  { id: 'products', name: 'Produtos', icon: 'IconShoppingCart', showIf: 'hasSalesCapability' },
  { id: 'llm', name: 'Modelo IA', icon: 'IconBrain' },
  { id: 'advanced', name: 'Avançado', icon: 'IconSettings' },
];

// ===============================================
// VALIDATION RULES
// ===============================================

export const VALIDATION = {
  agentName: { min: 3, max: 50 },
  businessName: { min: 2, max: 100 },
  description: { max: 500 },
  phone: { pattern: /^\(\d{2}\) \d{4,5}-\d{4}$/ },
  email: { pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ },
};

// ===============================================
// FILE UPLOAD LIMITS
// ===============================================

export const UPLOAD_LIMITS = {
  maxFileSize: 1 * 1024 * 1024, // 10MB
  allowedTypes: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'text/plain',
    'text/markdown',
    'text/x-markdown',
  ],
  allowedExtensions: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt', '.md'],
};

// ===============================================
// LEAD QUALIFICATION FRAMEWORKS
// ===============================================

export const QUALIFICATION_FRAMEWORKS = {
  simple: {
    id: 'simple',
    name: 'Simples',
    description: 'Qualificação básica com interesse e orçamento',
    fields: ['interest_level', 'budget_range', 'timeline'],
    fieldLabels: {
      interest_level: 'Nível de Interesse',
      budget_range: 'Faixa de Orçamento',
      timeline: 'Prazo para Decisão',
    },
  },
  bant: {
    id: 'bant',
    name: 'BANT',
    description: 'Budget, Authority, Need, Timeline',
    fields: ['budget', 'authority', 'need', 'timeline'],
    fieldLabels: {
      budget: 'Orçamento (Budget)',
      authority: 'Autoridade (Authority)',
      need: 'Necessidade (Need)',
      timeline: 'Prazo (Timeline)',
    },
  },
  meddic: {
    id: 'meddic',
    name: 'MEDDIC',
    description: 'Para vendas enterprise complexas',
    fields: ['metrics', 'economic_buyer', 'decision_criteria', 'decision_process', 'identify_pain', 'champion'],
    fieldLabels: {
      metrics: 'Métricas',
      economic_buyer: 'Comprador Econômico',
      decision_criteria: 'Critérios de Decisão',
      decision_process: 'Processo de Decisão',
      identify_pain: 'Dor Identificada',
      champion: 'Campeão Interno',
    },
  },
  champ: {
    id: 'champ',
    name: 'CHAMP',
    description: 'Challenges, Authority, Money, Prioritization',
    fields: ['challenges', 'authority', 'money', 'prioritization'],
    fieldLabels: {
      challenges: 'Desafios',
      authority: 'Autoridade',
      money: 'Dinheiro/Orçamento',
      prioritization: 'Priorização',
    },
  },
};

// ===============================================
// FOLLOWUP TYPES
// ===============================================

export const FOLLOWUP_TYPES = {
  lead_nurturing: {
    id: 'lead_nurturing',
    name: 'Nutrição de Leads',
    description: 'Acompanhamento de leads interessados',
    icon: 'IconSeeding',
  },
  abandoned_cart: {
    id: 'abandoned_cart',
    name: 'Carrinho Abandonado',
    description: 'Recuperação de carrinhos abandonados',
    icon: 'IconShoppingCartOff',
  },
  post_sale: {
    id: 'post_sale',
    name: 'Pós-Venda',
    description: 'Acompanhamento após a compra',
    icon: 'IconGift',
  },
  inactive_customer: {
    id: 'inactive_customer',
    name: 'Cliente Inativo',
    description: 'Reativação de clientes inativos',
    icon: 'IconUserOff',
  },
  no_response: {
    id: 'no_response',
    name: 'Sem Resposta',
    description: 'Follow-up para contatos sem resposta',
    icon: 'IconMessageOff',
  },
};

// ===============================================
// TIME OPTIONS
// ===============================================

export const TIME_OPTIONS = [
  '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30',
  '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
  '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
  '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30',
  '22:00',
];

export const WEEKDAY_OPTIONS = [
  { value: 'dom', label: 'Domingo' },
  { value: 'seg', label: 'Segunda' },
  { value: 'ter', label: 'Terça' },
  { value: 'qua', label: 'Quarta' },
  { value: 'qui', label: 'Quinta' },
  { value: 'sex', label: 'Sexta' },
  { value: 'sab', label: 'Sábado' },
];

// ===============================================
// SALES TECHNIQUES
// ===============================================

export const SALES_PRIMARY_TECHNIQUES = [
  { id: 'spin_selling', name: 'SPIN Selling', description: 'Perguntas sobre Situação, Problema, Implicação e Necessidade antes de oferecer soluções' },
  { id: 'consultative', name: 'Venda Consultiva', description: 'Agir como consultor, entender a dor e recomendar a melhor solução' },
  { id: 'solution_selling', name: 'Venda de Solução', description: 'Focar em como o produto resolve o problema específico do cliente' },
  { id: 'direct', name: 'Venda Direta', description: 'Apresentar produto e preço de forma objetiva, sem abordagem exploratória' },
];

export const SALES_STRATEGIES = [
  { id: 'upsell', name: 'Upsell', description: 'Sugerir versão superior ou mais completa' },
  { id: 'cross_sell', name: 'Cross-sell', description: 'Sugerir produtos complementares' },
  { id: 'urgency_scarcity', name: 'Urgência/Escassez', description: 'Criar senso de urgência quando houver promoções ou estoque limitado' },
  { id: 'social_proof', name: 'Prova Social', description: 'Mencionar aprovação de outros clientes' },
  { id: 'anchor_pricing', name: 'Preço-Âncora', description: 'Mostrar valor original antes do desconto' },
  { id: 'objection_handling', name: 'Contorno de Objeções', description: 'Tratar hesitações com argumentos construtivos' },
];

// Re-export ICON_MAP
export { ICON_MAP } from './iconMap';
